"use client";

import React, { useState, useRef, useEffect, useMemo, Suspense } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import Table from "../../components/table";
import tableStyles from "../../components/Table.module.css";
import styles from "../../styles/billing.module.css";
import Heading from "../../components/shared-modules/Heading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faShareAlt,
  faFileInvoice,
  faFilePdf,
  faSync,
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import { jsPDF } from "jspdf";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSummary } from "../../services/case-manager/billings";
import { getBillingCodes } from "../../services/case-manager/billing/setting";
import Button from "../../components/button-v1";
import {
  displayDate,
  formatNumber,
  formatLastFirstName,
  compareByLastName,
  capitalize
} from "../../utils/helper";
import {
  FiDownload,
  FiLock,
  FiUnlock,
  FiChevronDown,
  FiArrowUp,
  FiInfo,
  FiSearch
} from "react-icons/fi";
import { Edit2, MoreVertical } from "lucide-react";
import {
  updateBillingStatus,
  lockBillingToggle,
  getColumnPreferences,
  saveColumnPreferences as saveColumnPreferencesApi,
  searchBillingPatients
} from "../../services/case-manager/billings";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogFooter
} from "../../components/ui/dialog";
import { Dropdown, DropdownItem, DropdownSeparator } from "../../components/ui/dropdown";

import {
  Select,
  SelectTrigger,
  SelectItem,
  SelectContent,
  SelectValue
} from "../../components/ui/select";

import { useOnClickOutside } from "../../hooks/useClickOutside";
import Spinner from "../../components/spinner";
import toast from "react-hot-toast";
import {
  generateYears,
  convertUTCToLocal,
  displayDateAsIs,
  formatSecondsToTime
} from "../../utils/helper";
import { formatUSADate, formatUSADateTimeDash, formatUSADateDash } from "../../utils/dateFormatters";
import dayjs from "../../utils/dayjs-setup";
import Tooltip from "../../components/shared-modules/Tooltip";
import { getLayerZIndex } from "../../utils/layerManager";
import AppealLetterModal from "../../components/appeal-letter/AppealLetterModal";
import AppealLetterEmailForm from "../../components/appeal-letter/AppealLetterEmailForm";
import { shareAuditReport, uploadPatientDocumentsWithId, getPatientDocumentsById } from "../../services/patient/documents";
import { getPatientInfo } from "../../services/patient/settings";
import { buildReportFilename, DOCTYPES } from "../../utils/fileNaming";

const SharingModal = React.lazy(() =>
  import("../../components/shared-modules/share-modal")
);

// SCRUM-604: adapts a billing-summary patient row (patient_first_name /
// patient_last_name / patient_name) to the generic {firstName, lastName,
// fullName} shape the name-formatting/sorting helpers expect.
const nameFieldsOf = p => ({
  firstName: p?.patient_first_name,
  lastName: p?.patient_last_name,
  fullName: p?.patient_name
});

// Amounts shown in the card instead overflow the card if they exceed the card's width
const CURRENCY_GLYPH_EM = { ",": 0.3, ".": 0.3 };
const DEFAULT_GLYPH_EM = 0.62;

const measureCurrencyEm = text =>
  [...text].reduce(
    (total, char) => total + (CURRENCY_GLYPH_EM[char] ?? DEFAULT_GLYPH_EM),
    0
  );

const rpmCodes = ["99453", "99445", "99454", "99470", "99457", "99458"];
const ccmCodes = ["G0506", "99490", "99439", "99487", "99489"];

// Per direct request: if the patient search box's query is a DOB (digits
// only, optionally separated by "-" or "/" - a name search never matches
// this), normalize it to MM/DD/YYYY before it's sent to the backend,
// regardless of which separator (or none) was typed. Anything that isn't
// this shape (a name, MRN, etc.) passes through untouched.
const formatSearchQueryForBackend = rawQuery => {
  const trimmed = rawQuery.trim();
  if (!trimmed || !/^[\d\-/]+$/.test(trimmed)) return trimmed;

  const digitsOnly = trimmed.replace(/[-/]/g, "");
  let mm, dd, yyyy;

  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(trimmed)) {
    // e.g. "4-19-1955", "04-19-1955" - zero-pad month/day, normalize separators to "/"
    const [month, day, year] = trimmed.split(/[-/]/);
    mm = month.padStart(2, "0");
    dd = day.padStart(2, "0");
    yyyy = year;
  } else if (digitsOnly.length === 8) {
    // e.g. "04191955" - no separators at all
    mm = digitsOnly.slice(0, 2);
    dd = digitsOnly.slice(2, 4);
    yyyy = digitsOnly.slice(4, 8);
  } else {
    // Doesn't match a recognizable date shape - leave it as-is.
    return trimmed;
  }

  return `${mm}/${dd}/${yyyy}`;
};

// SCRUM-631: status word -> label map, used to pick each pill's color class
// (the pill itself displays the CPT code, not this word). Module-level since
// it's also used by the Mark as Denied/Appealed modal's code checkboxes.
const statusToLabelMap = {
  billed: "Billed",
  billable: "Billable",
  paid: "Paid",
  denied: "Denied",
  appealed: "Appealed",
  non_billable: "NonBillable",
  default: "NonBillable"
};

// SCRUM-685: (i) icon copy for the Deactivated/Inactive tags shown in the
// search bar's result dropdown - the *conceptual* definition of the status
// itself, not the per-patient date/reason (that's SCRUM-684's separate
// hover popup on the table row). No entry for "active" - only these two
// statuses get an icon per the ticket.
const SEARCH_STATUS_INFO = {
  deactivated:
    "Patients who were deactivated during the month currently being viewed. They still appear in the Billing Dashboard because they had billable activity that month.",
  inactive:
    "Patients who were deactivated in a prior month (long ago). They do not show any billing data in the Billing Dashboard."
};

// SCRUM-632: accent color for the row hover/info popover - matches the
// pill's own status colors (see .billed/.billable/.paid/.denied/.appealed
// in billing.module.css), so the popover and the pill it belongs to always
// read as the same status color.
const statusToAccentColor = {
  Billed: "#047857",
  Billable: "#854d0e",
  Paid: "#166534",
  Denied: "#b91c1c",
  Appealed: "#0369a1",
  NonBillable: "#64748b"
};
// Every Tooltip below (CPT code hover popover, Deactivated/Inactive tag
// popover, Billing status pill, disabled-code message, column-header info
// icons) needs to render above literally everything else on the page,
// including the app's own fixed navbar - Tooltip's default z-index is
// computed from the *trigger's nearest positioned ancestor* (see
// getParentZIndex in utils/layerManager.js), which for a table cell deep in
// a plain (non-positioned) row resolves to 0, well below the navbar's
// z-index. Passing the shared "tooltip" layer constant directly overrides
// that per-usage instead of relying on that ancestor-relative computation.
const TOOLTIP_Z_INDEX = getLayerZIndex("tooltip");
const PAGE_SIZE_OPTIONS = [50, 100, 150, 200];

// SCRUM-987: Status Count pills that filter the patient list, in display
// order. `key` is both the pill's color class suffix and the API's
// billing_status value; `summaryKey` is its count in status_summary.
const STATUS_COUNT_PILLS = [
  { key: "billable", label: "Billable", summaryKey: "total_billable" },
  { key: "billed", label: "Billed", summaryKey: "total_billed" },
  { key: "paid", label: "Paid", summaryKey: "total_paid" },
  { key: "denied", label: "Denied", summaryKey: "total_denied" },
  { key: "appealed", label: "Appealed", summaryKey: "total_appealed" }
];
const BILLING_STATUS_FILTERS = STATUS_COUNT_PILLS.map(pill => pill.key);

// SCRUM-3: Kalioundji gets a numbered-pagination footer (arrows + windowed
// page buttons with ellipsis, direct page-entry with inline validation)
// instead of the plain Back/[page box]/Next footer every other account
// keeps unchanged - see KalioundjiPagination below for the exact two-state
// (default vs. a page entered) windowing. Matched by acc_id rather than
// acc_name (as the Shaarawy/Shalita last-reading-column swap in
// careManagerDashboard.js does) since we have exact account IDs for this
// one - one per environment, since the account has a different id in
// production vs. staging. Re-introduces the same
// KALIOUNDJI_ACCOUNT_IDS/acc_id pattern SCRUM-690 originally used for a
// (since-removed) column layout, kept for this new, unrelated use.
const KALIOUNDJI_ACCOUNT_IDS = [
  "491faae8-fa4c-4364-97dc-e34f08f1a470", // production
  "1c194411-efac-4338-aaa4-b5fee812e4d3" // staging
];

// Merges a set of already-computed page-number groups (e.g. "first 2",
// "last 2", "current + next 2") into one sorted, deduped sequence, inserting
// "..." only where an actual gap remains between groups - same spirit as
// table.js's own "expanded" paginationVariant windowing, but built as a
// generic helper here since Kalioundji's two states (below) use differently
// sized groups, not one fixed scheme.
const buildKalioundjiPageWindow = (groups, totalPages) => {
  const uniqueSorted = Array.from(new Set(groups.flat()))
    .filter(p => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const pages = [];
  uniqueSorted.forEach((p, i) => {
    if (i > 0 && p - uniqueSorted[i - 1] > 1) pages.push("...");
    pages.push(p);
  });
  return pages;
};

// Entered state (AC9): first 2, the current page + its next 2, last 2 -
// forward-looking (not centered ±1), since that's the exact shape the
// ticket's own example gives (page 7 of 30 -> "1 2 ... [7] 8 9 ... 29 30").
// First-2/current-window/last-2 always merge into one gap-free run once
// totalPages <= 6 (AC11) - checked via the result's own "..." presence
// rather than a separate threshold constant.
const getKalioundjiEnteredPageWindow = (currentPage, totalPages) =>
  buildKalioundjiPageWindow(
    [
      [1, 2],
      [currentPage, currentPage + 1, currentPage + 2],
      [totalPages - 1, totalPages]
    ],
    totalPages
  );

// SCRUM-3: standalone so its own page-entry draft/error state doesn't
// re-render (or get reset by) the rest of this already-huge page on every
// keystroke. onPageChange is the same handlePageChange the plain footer
// uses below - reusing it (rather than a new handler) is what keeps a
// Kalioundji page jump behaving identically to every other account's
// underneath the different UI: same A-Z-state clearing, same N+1
// prefetch, and no filter state touched at all (so active filters survive
// a jump for free, same as they already do for everyone else).
//
// Two distinct display states, matching AC8/AC9 exactly:
// - Default (hasNavigated false): first 3 / last 3 page buttons, with an
//   EMPTY typeable box sitting in its own slot between the two ellipses -
//   no page is "current" yet, even though the table is already showing
//   page 1. Unlike the entered state below, the box here isn't standing in
//   for any specific page number, so it's built directly as its own three-
//   part sequence (first-3, box, last-3) rather than via
//   buildKalioundjiPageWindow's single-gap merge.
// - Entered (hasNavigated true, set the moment the user commits a typed
//   page or clicks an arrow/page button): first 2 / last 2, with that same
//   box now standing in for the current page's own slot in the merged
//   window - boxed/highlighted, immediately followed by its next-2 plain
//   buttons with no separating ellipsis (they're adjacent in the window).
// Either state collapses to plain, un-boxed buttons for every page with no
// ellipsis and no box at all once the whole range already fits (AC11).
// resetKey ties back to the exact same deps that reset `page` to 1 on the
// parent (billingType/patientStatus/summaryDate/pageSize) - a fresh view
// should start back in the default state, not carry over a previous view's
// "already navigated" box.
//
// A third state, browsing (arrow or page-button navigation): the current
// page renders as a highlighted plain button followed by its next 2, and the
// box stays an EMPTY "Enter Page #" box (no edit icon) in the gap - just
// before the current page's run when there's a gap before it
// ("1 2 ... [box] 9 10 11 ... 14 15"), otherwise in the gap after it
// ("1 2 3 4 ... [box] ... 14 15").
const KALIOUNDJI_PAGINATION_MODE = {
  DEFAULT: "default",
  ENTERED: "entered",
  BROWSING: "browsing"
};

const KalioundjiPagination = ({ currentPage, totalPages, isFetching, onPageChange, resetKey }) => {
  const [pageInput, setPageInput] = useState("");
  const [isEditingInput, setIsEditingInput] = useState(false);
  const [mode, setMode] = useState(KALIOUNDJI_PAGINATION_MODE.DEFAULT);
  const hasNavigated = mode === KALIOUNDJI_PAGINATION_MODE.ENTERED;
  const safeCurrentPage = currentPage || 1;
  const safeTotalPages = totalPages || 1;

  useEffect(() => {
    setPageInput("");
    setIsEditingInput(false);
    setMode(KALIOUNDJI_PAGINATION_MODE.DEFAULT);
  }, [resetKey]);

  // A successful navigation from ANY control (arrows, a page-number
  // button, or the box) means a leftover draft is no longer relevant, and
  // the box should go back to showing (not editing) the now-current page.
  useEffect(() => {
    setPageInput("");
    setIsEditingInput(false);
  }, [safeCurrentPage]);

  // Arrows and page buttons browse; only a typed page puts the box in the
  // entered state.
  const navigateTo = (newPage, nextMode = KALIOUNDJI_PAGINATION_MODE.BROWSING) => {
    setMode(nextMode);
    if (newPage !== safeCurrentPage) onPageChange(newPage);
  };

  // Only ever validates/navigates on an actual submit (blur or Enter, which
  // blurs) - never mid-typing, and a blur with nothing typed is a silent
  // no-op rather than an error, so simply clicking away from an empty box
  // isn't treated as an invalid entry. A page that doesn't exist (0 or past
  // the last page) is reported in a toast - an inline message under the
  // footer gets clipped - and the box goes back to its previous state.
  const commitPageInput = () => {
    const typed = pageInput.trim();
    setPageInput("");
    setIsEditingInput(false);
    if (!typed) return;
    const parsed = Number(typed);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > safeTotalPages) {
      toast.error(
        `Page ${parsed} does not exist. The last page is ${safeTotalPages}.`,
        { id: "kalioundji-page-not-found" }
      );
      return;
    }
    navigateTo(parsed, KALIOUNDJI_PAGINATION_MODE.ENTERED);
  };

  const pageButton = p => (
    <button
      key={p}
      type="button"
      className={`${styles.footerPaginationControl} ${styles.kalioundjiPaginationControl} ${
        styles.kalioundjiPageButton
      } ${p === safeCurrentPage ? styles.kalioundjiPageButtonActive : ""}`}
      onClick={() => navigateTo(p)}
      disabled={isFetching || p === safeCurrentPage}
      aria-current={p === safeCurrentPage ? "page" : undefined}
    >
      {p}
    </button>
  );
  const ellipsis = key => (
    <span key={key} className={styles.kalioundjiEllipsis}>
      …
    </span>
  );
  // Once a page's been entered, the box shows that page number styled like
  // the active page button (navy fill) - which reads as a static "you are
  // here" label unless something signals it's still a real, clickable
  // input. A title tooltip plus a pencil icon (hidden while actively
  // typing, where the hint's no longer needed) cover that without relying
  // on a user discovering it by trial and error.
  const showEditHint = hasNavigated && !isEditingInput;
  const box = (
    <div key="box" className={styles.kalioundjiPageInputWrap}>
      <input
        type="text"
        inputMode="numeric"
        aria-label="Go to page"
        placeholder="Enter Page #"
        title={hasNavigated ? "Click to enter a different page" : undefined}
        className={`${styles.footerPageBox} ${styles.footerPageInput} ${styles.kalioundjiPageInput} ${
          hasNavigated ? styles.kalioundjiPageInputActive : ""
        }`}
        value={isEditingInput ? pageInput : hasNavigated ? String(safeCurrentPage) : ""}
        onFocus={() => {
          setIsEditingInput(true);
          setPageInput("");
        }}
        onChange={e => setPageInput(e.target.value.replace(/[^0-9]/g, ""))}
        onKeyDown={e => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        onBlur={commitPageInput}
        disabled={isFetching}
      />
      {showEditHint && (
        <Edit2
          size={11}
          className={styles.kalioundjiPageInputEditIcon}
          aria-hidden="true"
        />
      )}
    </div>
  );

  const renderSequence = () => {
    if (mode === KALIOUNDJI_PAGINATION_MODE.BROWSING) {
      const window = getKalioundjiEnteredPageWindow(safeCurrentPage, safeTotalPages);
      // AC11: no gap left means nowhere for the box; plain buttons only.
      if (!window.includes("...")) return window.map(pageButton);
      const currentIdx = window.indexOf(safeCurrentPage);
      const gapBeforeCurrent = window[currentIdx - 1] === "...";
      const boxGapIdx = gapBeforeCurrent ? currentIdx - 1 : window.indexOf("...");
      return window.flatMap((p, i) => {
        if (i === boxGapIdx) {
          return gapBeforeCurrent
            ? [ellipsis(`ellipsis-${i}`), box]
            : [ellipsis(`ellipsis-${i}`), box, ellipsis(`ellipsis-${i}-after`)];
        }
        return p === "..." ? ellipsis(`ellipsis-${i}`) : pageButton(p);
      });
    }

    if (!hasNavigated) {
      // AC11 (extended to the default state too): first-3 ∪ last-3 already
      // covers 1..totalPages with no gap once totalPages <= 6 - nothing for
      // a box to split, so every page renders as a plain button instead.
      if (safeTotalPages <= 6) {
        return Array.from({ length: safeTotalPages }, (_, i) => pageButton(i + 1));
      }
      const first = [1, 2, 3].filter(p => p <= safeTotalPages);
      const last = [safeTotalPages - 2, safeTotalPages - 1, safeTotalPages].filter(
        p => p > (first[first.length - 1] || 0)
      );
      return [
        ...first.map(pageButton),
        ellipsis("ellipsis-before"),
        box,
        ellipsis("ellipsis-after"),
        ...last.map(pageButton)
      ];
    }

    const window = getKalioundjiEnteredPageWindow(safeCurrentPage, safeTotalPages);
    // AC11: once the merged window has no gap left (totalPages <= 6), every
    // page - including the current one - renders as a plain button instead
    // of the box; pageButton's own active-class check still highlights it.
    const hasGap = window.includes("...");
    return window.map((p, i) =>
      p === "..."
        ? ellipsis(`ellipsis-${i}`)
        : hasGap && p === safeCurrentPage
        ? box
        : pageButton(p)
    );
  };

  return (
    <div className={styles.kalioundjiPagination}>
      <button
        type="button"
        className={`${styles.footerPaginationControl} ${styles.kalioundjiPaginationControl}`}
        onClick={() => navigateTo(safeCurrentPage - 1)}
        disabled={safeCurrentPage <= 1 || isFetching}
        aria-label="Previous page"
      >
        <FontAwesomeIcon icon={faChevronLeft} size="xs" />
      </button>

      {renderSequence()}

      <button
        type="button"
        className={`${styles.footerPaginationControl} ${styles.kalioundjiPaginationControl}`}
        onClick={() => navigateTo(safeCurrentPage + 1)}
        disabled={safeCurrentPage >= safeTotalPages || isFetching}
        aria-label="Next page"
      >
        <FontAwesomeIcon icon={faChevronRight} size="xs" />
      </button>
    </div>
  );
};

// SCRUM-898: how a code's status change moves the "Billed this month" and
// "Total Billable" cards, by the bill's `amount`. Any other transition
// (e.g. Appealed -> Paid) leaves both cards as they are.
const getBillingAmountDelta = (fromStatus, toStatus, amount) => {
  if (!amount) return null;
  if (fromStatus === "billable" && toStatus === "billed")
    return { billed: amount, billable: -amount };
  if (fromStatus === "billed" && toStatus === "paid")
    return { billed: -amount, billable: 0 };
  if (fromStatus === "paid" && toStatus === "billable")
    return { billed: 0, billable: amount };
  return null;
};

// One-time codes only count toward the cards in the month they were last
// billable; in any other selected month their status change is skipped.
const ONE_TIME_BILLING_CODES = ["99453", "G0506"];
const isLastBillableInMonth = (billing, year, month) => {
  const lastBillable = dayjs(billing?.last_billable_date || billing?.last_billable);
  return (
    lastBillable.isValid() &&
    lastBillable.year() === Number(year) &&
    lastBillable.month() + 1 === Number(month)
  );
};
const skipsBillingAmount = (billing, year, month) =>
  ONE_TIME_BILLING_CODES.includes(String(billing?.billing_code)) &&
  !isLastBillableInMonth(billing, year, month);

// Adds in cents so repeated changes don't drift (0.1 + 0.2), and never
// shows a negative total.
const addAmount = (total, delta) =>
  Math.max(0, Math.round(((Number(total) || 0) + delta) * 100) / 100);

// SCRUM-627/732: Jira-style "edit columns" control in the table header -
// lets the user hide/show these specific columns. Name and Actions are
// always shown (not user-configurable), and the CPT Codes columns are
// already controlled separately by the RPM/CCM Billing pills, so they're
// excluded here too.
// Name and Actions are never in this list (always shown - identity and
// the row menu aren't optional). CPT Codes (RPM)/(CCM) are also
// deliberately excluded - those are already toggled by the RPM/CCM
// Billing pills, a more prominent, intentional control than this picker;
// adding a second, redundant way to hide/show them here would just be
// confusing. Everything else, including the pre-redesign RPM/CCM columns
// restored per direct request, is configurable.
// `group` drives two things: (1) which entries the picker popup even
// offers - "generic" always, "ccm"/"rpm" only while that program's tab is
// selected (both show when both tabs are), and (2) the StaffColumnPreference
// API's per-user default the first time nothing's been saved yet: generic
// columns default visible, program-specific detail columns default hidden.
// Billing summary row's place of service for that month. `facility` carries
// the same name and is kept for one release before the backend removes it.
const placeOfServiceName = row =>
  row?.place_of_service?.name || row?.facility || "--";

const CONFIGURABLE_COLUMNS = [
  { key: "facility_provider", label: "Place of Service / Provider", group: "generic" },
  { key: "insurance", label: "Insurance", group: "generic" },
  { key: "icd_codes_grouped", label: "ICD-10", group: "generic" },
  { key: "dos", label: "DOS", group: "generic" },
  { key: "ccm_consent_date", label: "Consent", group: "generic" },
  { key: "ccm_monitoring_time", label: "CCM Time", group: "ccm" },
  { key: "rpm_monitoring_time", label: "RPM Time", group: "rpm" },
  { key: "rolling_reading_days", label: "Days", group: "rpm" },
  { key: "rpm_setup_date", label: "Setup Date", group: "rpm" },
  { key: "rpm_anchor_date", label: "Anchor Date", group: "rpm" }
];

// The StaffColumnPreference API stores preferences per (staff, view_name) -
// this is the view_name for the Billing Dashboard's patient table.
const COLUMN_PREFERENCES_VIEW_NAME = "billing_summary";

// Default the first time a user has no saved preference yet (API returns
// columns: null) - matches the Figma "Ready" frame's column-picker default
// (Name/Facility/Provider/Insurance/ICD-10/DOS visible; the CCM/RPM detail
// columns start hidden until a user opts in via Save).
const DEFAULT_VISIBLE_COLUMNS = Object.fromEntries(
  CONFIGURABLE_COLUMNS.map(col => [col.key, col.group === "generic"])
);
const months = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" }
];

// Debounce a rapidly-changing value (e.g. the search box) so we don't hit
// the search endpoint on every keystroke.
function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function BillingPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  // SCRUM-3: read fresh every render (not memoized/cached in state) so
  // switching accounts - which re-renders this page via the account
  // switcher - picks up the new value immediately, same as the
  // Shaarawy/Shalita acc_name check in careManagerDashboard.js does.
  const isKalioundjiAccount = KALIOUNDJI_ACCOUNT_IDS.includes(
    sessionStorage.getItem("acc_id")
  );
  // SCRUM-687: restore the billing view (tab/month/year/status/page/page
  // size) from the URL when arriving via a "Back to Billing" link - read
  // once on mount, not kept in sync afterward.
  const initialBillingParams = useRef(
    new URLSearchParams(window.location.search)
  ).current;
  const [selectedViews, setSelectedViews] = useState(() => {
    const view = initialBillingParams.get("view");
    if (view === "ccm") return { ccm: true, rpm: false };
    if (view === "all") return { ccm: true, rpm: true };
    // Default landing view is RPM Billing only - a restored ?view=rpm
    // (or no view param at all, i.e. a fresh landing) both fall here.
    return { ccm: false, rpm: true };
  });
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  // SCRUM-627: single merged month+year picker (Figma node 5316:2889,
  // "July 2026" one pill/one panel) - one open state for one panel, instead
  // of two independent Select dropdowns each opening on their own.
  const [monthYearOpen, setMonthYearOpen] = useState(false);
  const monthYearRef = useOnClickOutside(() => setMonthYearOpen(false));
  // SCRUM-634/635: row 3-dots menu -> "Mark as Denied"/"Mark as Appealed"
  // popup state.
  const [markStatusModal, setMarkStatusModal] = useState({
    open: false,
    mode: null, // "denied" | "appealed"
    row: null,
    selectedIds: []
  });
  const [date, setDate] = useState(new Date());
  const [patientId, setPatientId] = useState(null);
  const [updateStatusCode, setUpdateStatusCode] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // SCRUM-687: derived from the same restored `view` param as
  // selectedViews below (rather than a hardcoded "rpm" default corrected
  // after mount by the selectedViews->billingType effect further down) -
  // otherwise, restoring to the CCM or "All" tab meant billingType changed
  // out from under skipInitialPageReset's one-time skip guard right after
  // mount, re-triggering that effect and resetting `page` back to 1 before
  // the user ever saw it. Landing back on RPM (the default) never
  // triggered that change, which is why this bug was specific to
  // returning to a non-default tab.
  const [billingType, setBillingType] = useState(() => {
    const view = initialBillingParams.get("view");
    if (view === "ccm") return "ccm";
    if (view === "all") return "all";
    // Matches selectedViews' default above - RPM Billing only on a fresh
    // landing (no ?view= param), same as a restored ?view=rpm.
    return "rpm";
  });
  // SCRUM-594: default to last month (the most recently completed billing
  // period) instead of the current month, so users see finalized data
  // without manually changing the filter. Computed in Pacific Time
  // (America/Los_Angeles, i.e. PST/PDT — dayjs's tz() resolves the correct
  // one automatically for the given date) specifically, not the viewer's
  // system/browser clock — a billing period boundary must land on the same
  // calendar day for every user regardless of where they're logged in from.
  const lastMonthPT = dayjs().tz("America/Los_Angeles").subtract(1, "month");
  const [month, setMonth] = useState(() => {
    const restored = Number(initialBillingParams.get("month"));
    return restored >= 1 && restored <= 12 ? restored : lastMonthPT.month() + 1; // dayjs months are 0-indexed
  });
  const [year, setYear] = useState(() => {
    const restored = Number(initialBillingParams.get("year"));
    return restored > 2000 ? restored : lastMonthPT.year();
  });
  const [sharing, setSharing] = useState(false);
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const [auditReportYear, setAuditReportYear] = useState(
    new Date().getFullYear()
  );
  const [auditReportMonth, setAuditReportMonth] = useState(
    new Date().getMonth() + 1
  );
  // Bug fix: the audit report month picker listed all 12 months regardless
  // of the selected year - generateYears(2024) already stops the year list
  // at the current year, but nothing stopped a future month from being
  // picked within that same current year (e.g. selecting December while
  // still in September). Past years still offer all 12 months.
  const auditReportAvailableMonths = useMemo(() => {
    const now = new Date();
    if (Number(auditReportYear) !== now.getFullYear()) return months;
    const currentMonthNum = now.getMonth() + 1;
    return months.filter(month => Number(month.value) <= currentMonthNum);
  }, [auditReportYear]);

  // Clamp an already-selected future month back to the current month if
  // the year changes to the current year and that selection is no longer
  // valid (rather than silently keeping a future selection off-screen).
  useEffect(() => {
    const now = new Date();
    if (Number(auditReportYear) !== now.getFullYear()) return;
    const currentMonthNum = now.getMonth() + 1;
    if (Number(auditReportMonth) > currentMonthNum) {
      setAuditReportMonth(currentMonthNum);
    }
  }, [auditReportYear]);
  const [updateStatus, setUpdateStatus] = useState({
    patientId: null,
    code: null,
    status: null,
    id: null
  });
  const [selectedReportType, setSelectedReportType] = useState("");
  // SCRUM-822: default the dashboard to active patients (still overridable
  // via the picker, or restored to whatever was last selected via the URL's
  // patient_status param - see initialBillingParams above).
  const [patientStatus, setPatientStatus] = useState(
    () => initialBillingParams.get("patient_status") || "active"
  );
  // SCRUM-987: the selected Status Count pill (null = full list). One at a
  // time; restored from the URL on "Back to Billing" like the other filters,
  // so a restored page number still belongs to the same filtered list.
  const [billingStatus, setBillingStatus] = useState(() => {
    const restored = initialBillingParams.get("billing_status");
    return BILLING_STATUS_FILTERS.includes(restored) ? restored : null;
  });
  const [page, setPage] = useState(() => {
    const restored = Number(initialBillingParams.get("page"));
    return restored > 0 ? restored : 1;
  });
  // Bug fix: page size was persisted to localStorage and re-read on every
  // mount, so a plain browser refresh kept whatever size was last picked
  // instead of resetting to the 100 default. `page_size` in the URL is a
  // separate, intentional case - restoring the exact view (including size)
  // when navigating back from a patient's chart via "Back to Billing" - so
  // that restoration path is untouched; only the always-on localStorage
  // fallback is removed.
  const [pageSize, setPageSize] = useState(() => {
    const restoredPageSize = Number(initialBillingParams.get("page_size"));
    return PAGE_SIZE_OPTIONS.includes(restoredPageSize) ? restoredPageSize : 100;
  });

  const handlePageSizeChange = newSize => {
    setPageSize(newSize);
  };

  // SCRUM-683: A-Z nav - which letter (if any) is currently driving the
  // page landed on, and which patient (the response's "highlight": true
  // row) to scroll to and flash once that page's data arrives.
  const [jumpToLetter, setJumpToLetter] = useState(null);
  const [highlightedPatientId, setHighlightedPatientId] = useState(null);

  // SCRUM-683/687: which letter to show bolded in the A-Z bar - a separate,
  // purely cosmetic state from jumpToLetter above. jumpToLetter drives the
  // actual fetch (jump_to_letter param) and is deliberately never restored
  // from the URL, since re-sending it on "Back to Billing" could land on a
  // *different* page than the one just restored (the billing-summary
  // endpoint's pagination isn't stable across requests - see
  // project_scrum626_billing_dashboard memory). activeLetter has no such
  // risk since it never touches the query - it's just "which letter was
  // active when the user left", restored via buildBillingBackUrl's
  // `active_letter` param same as everything else, so the bar still shows
  // the right letter bolded after a round trip to a patient's chart.
  const [activeLetter, setActiveLetter] = useState(
    () => initialBillingParams.get("active_letter") || null
  );

  // SCRUM-687: "Back to Billing" also restores which patient row to scroll
  // back to (see buildBillingBackUrl/handlePatientNameClick), read once on
  // mount same as the other restored view params above. Consumed (cleared)
  // the first time it's matched against a loaded page - see the effect
  // near patientsList below - so it doesn't keep re-triggering on every
  // later refetch of that same page.
  const pendingScrollToPatientId = useRef(
    initialBillingParams.get("scroll_to_patient_id")
  );

  // SCRUM-732: which CONFIGURABLE_COLUMNS the user has chosen to show,
  // loaded from the StaffColumnPreference API (view_name="billing_summary").
  // Preferences are independent per billing_type ("rpm"/"ccm"/"all") -
  // `visibleColumnsByType` holds all three, `visibleColumns` below derives
  // the current tab's entry. `pendingVisibleColumns` is the popup's own
  // in-progress checkbox state (for whichever tab is active when it's
  // opened), only copied into visibleColumnsByType (and POSTed) when the
  // user clicks Save - unchecking boxes and closing the popup without
  // saving leaves the table untouched.
  const [visibleColumnsByType, setVisibleColumnsByType] = useState({
    rpm: DEFAULT_VISIBLE_COLUMNS,
    ccm: DEFAULT_VISIBLE_COLUMNS,
    all: DEFAULT_VISIBLE_COLUMNS
  });
  const visibleColumns = visibleColumnsByType[billingType] || DEFAULT_VISIBLE_COLUMNS;
  const [pendingVisibleColumns, setPendingVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);

  const columnPreferencesQuery = useQuery(
    ["billing-column-preferences", COLUMN_PREFERENCES_VIEW_NAME, billingType],
    () => getColumnPreferences(COLUMN_PREFERENCES_VIEW_NAME, billingType)
  );

  useEffect(() => {
    if (!columnPreferencesQuery.data) return;
    // Response shape: { columns: [...] } - a flat list already scoped to
    // whichever billing_type this fetch requested (the server filters by
    // the query param), not an object keyed by every tab.
    const savedColumns = columnPreferencesQuery.data.columns;
    const next = Array.isArray(savedColumns)
      ? Object.fromEntries(
          CONFIGURABLE_COLUMNS.map(col => [col.key, savedColumns.includes(col.key)])
        )
      : DEFAULT_VISIBLE_COLUMNS;
    setVisibleColumnsByType(prev => ({ ...prev, [billingType]: next }));
    setPendingVisibleColumns(next);
  }, [columnPreferencesQuery.data, billingType]);

  const togglePendingColumn = key => {
    setPendingVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const saveColumnPreferencesMutation = useMutation(
    columns => saveColumnPreferencesApi(COLUMN_PREFERENCES_VIEW_NAME, columns, billingType),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["billing-column-preferences", COLUMN_PREFERENCES_VIEW_NAME]);
      }
    }
  );

  const handleSaveColumns = () => {
    setVisibleColumnsByType(prev => ({ ...prev, [billingType]: pendingVisibleColumns }));
    const columnsToSave = CONFIGURABLE_COLUMNS.filter(
      col => pendingVisibleColumns[col.key]
    ).map(col => col.key);
    saveColumnPreferencesMutation.mutate(columnsToSave);
    setColumnsMenuOpen(false);
  };

  const handleResetColumns = () => {
    setPendingVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  // The "edit columns" popover is portaled to document.body (see its JSX
  // below) rather than positioned inline, because the header now lives in
  // .split_header_scroll (overflow:hidden, for the frozen-column scroll
  // sync fix) - an inline-positioned popover would get clipped the moment
  // it extended past that box. Position is computed from the trigger
  // button's own bounding rect, and outside-click checks both the trigger
  // and the portaled content (useOnClickOutside only tracks one element,
  // which doesn't cover a portal - same reasoning as Select's own
  // contentRef handling in components/ui/select).
  const columnsEditTriggerRef = useRef(null);
  const columnsMenuContentRef = useRef(null);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [columnsMenuPos, setColumnsMenuPos] = useState({ top: 0, left: 0 });

  // SCRUM-793: positions the popup relative to the *viewport* (matching
  // position: fixed below), not the document - the trigger lives in the
  // table's sticky header, so its viewport position is only stable once
  // scrolled into its stuck position, and can still shift before/around
  // that point. A one-time snapshot (previously position:absolute with a
  // +window.scrollY offset baked in) drifted away from the trigger as soon
  // as the page scrolled any further; recomputing on every scroll keeps it
  // glued to the icon regardless of whether the header is currently stuck.
  const updateColumnsMenuPos = () => {
    const rect = columnsEditTriggerRef.current?.getBoundingClientRect();
    if (rect) {
      setColumnsMenuPos({
        top: rect.bottom + 4,
        left: rect.right - 220
      });
    }
  };

  const openColumnsMenu = () => {
    updateColumnsMenuPos();
    // Re-sync pending checkboxes to the last-saved state on every open, so
    // reopening after a close-without-saving doesn't resurrect unsaved edits.
    if (!columnsMenuOpen) setPendingVisibleColumns(visibleColumns);
    setColumnsMenuOpen(open => !open);
  };

  useEffect(() => {
    if (!columnsMenuOpen) return;
    const handleClickOutside = e => {
      const inTrigger = columnsEditTriggerRef.current?.contains(e.target);
      const inMenu = columnsMenuContentRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) setColumnsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    // SCRUM-793: keep the popup glued to the trigger icon while open,
    // instead of the one-time position it used to freeze at on open.
    // capture:true matches Tooltip.js's own scroll-tracking, since the
    // relevant scroll can happen on an ancestor, not just window.
    window.addEventListener("scroll", updateColumnsMenuPos, true);
    window.addEventListener("resize", updateColumnsMenuPos);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updateColumnsMenuPos, true);
      window.removeEventListener("resize", updateColumnsMenuPos);
    };
  }, [columnsMenuOpen]);

  // Now that the table uses disableInternalScroll (the page itself is the
  // one scroller, per direct request, instead of a bounded inner region),
  // a long patient list can put the top of the page - month/KPI tiles,
  // "Billings" title - a long way above the viewport. This floating
  // button scrolls back to it once the user has scrolled down a bit.
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const summaryDate = `${year}-${month}`;

  // Powers the "CPT Codes Logic" info-icon popup on the RPM/CCM column
  // headers below - the full code+description catalog, filtered per
  // program from its `category` field.
  const billingCodesQuery = useQuery(
    ["billing-codes"],
    () => getBillingCodes("all")
  );

  // SCRUM-685/724/629: patient search box - a live typeahead dropdown as the
  // user types (debounced so we don't hit the search endpoint on every
  // keystroke), plus a search-icon click that re-runs the main paginated
  // list with the query text as an extra param.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const searchContainerRef = useRef(null);
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  // The API 400s on anything shorter than 3 chars.
  const searchEnabled = debouncedSearchQuery.trim().length >= 3;

  const {
    data: searchResults,
    isFetching: isSearchFetching
  } = useQuery(
    ["billing-patient-search", debouncedSearchQuery, locationId, summaryDate, patientStatus],
    () =>
      searchBillingPatients(
        formatSearchQueryForBackend(debouncedSearchQuery),
        locationId,
        summaryDate,
        patientStatus
      ),
    { enabled: searchEnabled, keepPreviousData: true }
  );
  const searchPatients = searchResults?.patients || [];
  // SCRUM-685: real-time dropdown shows at most 10 matches, with a "Showing
  // 10 of N" indicator when there are more - `searchPatients` (unsliced)
  // still carries the true match count for that indicator.
  const visibleSearchPatients = searchPatients.slice(0, 10);

  useEffect(() => {
    if (!searchDropdownOpen) return;
    const handleClickOutside = e => {
      if (!searchContainerRef.current?.contains(e.target)) setSearchDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchDropdownOpen]);

  // Two distinct ways the search box re-queries the same billing-summary
  // list (not the typeahead dropdown's own search endpoint above): the
  // search-icon click sends `q`, selecting a dropdown result sends
  // `patient_id` instead - only one of the two is ever active at once, so
  // setting either clears the other. Both flow into getSummary/the query
  // key below.
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [activeSearchPatientId, setActiveSearchPatientId] = useState(null);

  const handleSearchIconClick = () => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 3) return;
    setSearchDropdownOpen(false);
    setActiveSearchPatientId(null);
    setActiveSearchTerm(formatSearchQueryForBackend(trimmed));
  };

  // Selecting a result keeps their name showing in the box (rather than
  // clearing it) so it's visible what's currently searched/selected -
  // standard combobox behavior. Editing it from there re-triggers the
  // typeahead dropdown as normal (searchQuery driving debouncedSearchQuery
  // above); clearing it back to empty resets to the full list (see the
  // effect below), same as any other search bar.
  const handleSelectSearchResult = patient => {
    setSearchDropdownOpen(false);
    setSearchQuery(patient.patient_name || "");
    setActiveSearchTerm("");
    setActiveSearchPatientId(patient.patient_id);
  };

  // Clearing the search box entirely (typing a search then deleting it all,
  // or clearing a selected patient's name) drops back to the unfiltered
  // list automatically - matches how search bars conventionally behave,
  // without needing a separate explicit "clear" action.
  useEffect(() => {
    if (searchQuery.trim() === "" && (activeSearchTerm || activeSearchPatientId)) {
      setActiveSearchTerm("");
      setActiveSearchPatientId(null);
    }
  }, [searchQuery, activeSearchTerm, activeSearchPatientId]);

  // Paged query for the table view, with background prefetch of the next
  // page (see onSuccess below) so clicking "next" feels instant.
  const {
    data: billingData,
    error,
    isLoading: isBillingSummaryLoading,
    isFetching: isBillingSummaryFetching
  } = useQuery(
    [
      "case-manager-billing-summary",
      summaryDate,
      billingType,
      patientStatus,
      // SCRUM-683: while a letter is driving navigation, the actual page
      // number is unknown until the response comes back (backend picks it),
      // so the letter itself - not `page` - identifies this fetch.
      jumpToLetter ? `letter:${jumpToLetter}` : page,
      pageSize,
      activeSearchTerm,
      activeSearchPatientId,
      // SCRUM-987: last, so the [1]-[3] filter-combo predicates in the
      // status/lock mutations below still match every cached variant.
      billingStatus
    ],
    () =>
      getSummary(
        summaryDate,
        billingType,
        locationId,
        undefined,
        undefined,
        patientStatus,
        page,
        true, // Enable pagination for the table view
        pageSize,
        jumpToLetter,
        activeSearchTerm,
        activeSearchPatientId,
        billingStatus
      ),
    {
      keepPreviousData: true,
      onSuccess: () => {
        setUpdateStatusCode(null);
        setUpdateStatus({
          patientId: null,
          code: null,
          status: null,
          id: null
        });
      }
    }
  );

  // Reset to page 1 whenever a filter or the page size changes - skips its
  // own first run so it doesn't clobber a page restored from the URL
  // (SCRUM-687 "Back to Billing").
  const skipInitialPageReset = useRef(true);
  useEffect(() => {
    if (skipInitialPageReset.current) {
      skipInitialPageReset.current = false;
      return;
    }
    setPage(1);
    // SCRUM-683: switching tab/status/month is a fresh view - any in-flight
    // A-Z jump (and its bolded letter) no longer applies to it.
    setJumpToLetter(null);
    setActiveLetter(null);
  }, [billingType, patientStatus, summaryDate, pageSize, billingStatus]);

  // SCRUM-987: picking a different month or year clears the Status Count
  // filter, so the full list for the new month shows. Done here (not in an
  // effect on summaryDate) so the change is one batched render and one
  // request, rather than a fetch with the old filter followed by another.
  // Loose comparison: picker values and the restored URL month may differ
  // in type (string vs number) for the same month.
  const handleMonthYearChange = (newMonth, newYear) => {
    // eslint-disable-next-line eqeqeq
    if (newMonth != month || newYear != year) setBillingStatus(null);
    setMonth(newMonth);
    setYear(newYear);
  };

  // Clicking the selected pill again clears it; any other pill replaces it.
  const handleStatusPillClick = status => {
    setBillingStatus(current => (current === status ? null : status));
  };

  // Background prefetch of page 2 once page 1 first loads. This covers only
  // the initial mount, where there's no click to hang an imperative prefetch
  // off of (see handlePageChange below, which imperatively prefetches N+1
  // for every subsequent page-number/Back/Next click - the deterministic
  // mechanism this effect can't provide on its own, since useQuery's
  // onSuccess doesn't fire when a page is served from an already-warm
  // cache). Harmless no-op if handlePageChange already covered it.
  useEffect(() => {
    // SCRUM-683: skip while a letter is driving the current page - the real
    // page number isn't known locally until this response's `meta` comes
    // back (see handlePageChange, which takes over prefetching once the
    // user pages normally from there).
    if (jumpToLetter) return;
    const totalPages = billingData?.meta?.total_pages;
    if (totalPages && page + 1 <= totalPages) {
      queryClient.prefetchQuery(
        ["case-manager-billing-summary", summaryDate, billingType, patientStatus, page + 1, pageSize, billingStatus],
        () =>
          getSummary(
            summaryDate,
            billingType,
            locationId,
            undefined,
            undefined,
            patientStatus,
            page + 1,
            true,
            pageSize,
            undefined,
            undefined,
            undefined,
            billingStatus
          ),
        { staleTime: 30 * 1000 } // avoids an unnecessary refetch if the user clicks through quickly
      );
    }
  }, [billingData, page, pageSize, summaryDate, billingType, patientStatus, locationId, queryClient, jumpToLetter, billingStatus]);

  // Derive summary/patients/meta from the current page's flat response.
  // Totals (billed_this_month/total_billable/status_summary/
  // location_billing_lock) are computed whole-tenant server-side, so
  // they're identical regardless of which page is currently loaded.
  const billingSummary = useMemo(() => {
    const results = billingData?.results || billingData || {};
    return {
      location_billing_lock: results.location_billing_lock,
      billed_this_month: results.billed_this_month,
      total_billable: results.total_billable,
      status_summary: results.status_summary
    };
  }, [billingData]);

  // Bug fix: `keepPreviousData: true` on the query above is what makes
  // pagination (Back/Next/a page number) feel instant - the old page's
  // rows stay on screen while the new page loads instead of flashing to
  // empty. But it applies uniformly to the *whole* query key, so switching
  // filters (e.g. Active Patients -> Inactive Patients) also kept showing
  // the previous filter's rows for the ~2-3s the new request took, before
  // snapping to the real (possibly empty) result - reading as "records
  // appear then disappear" rather than "list is loading". Tracked
  // separately here: only the filter-defining part of the key (not
  // page/pageSize/jumpToLetter, which SHOULD keep showing stale data
  // while paging) - `patientsList` blanks out the moment any of these
  // change, until the matching response actually lands.
  const currentFilterKey = `${summaryDate}|${billingType}|${patientStatus}|${activeSearchTerm}|${activeSearchPatientId}|${billingStatus}`;
  const settledFilterKeyRef = useRef(currentFilterKey);
  const isFilterTransitioning = settledFilterKeyRef.current !== currentFilterKey;
  useEffect(() => {
    if (!isBillingSummaryFetching) {
      settledFilterKeyRef.current = currentFilterKey;
    }
  }, [isBillingSummaryFetching, currentFilterKey]);

  const patientsList = useMemo(() => {
    if (isFilterTransitioning) return [];
    const results = billingData?.results || billingData || {};
    return results?.patients || [];
  }, [billingData, isFilterTransitioning]);

  const meta = useMemo(
    () => (isFilterTransitioning ? {} : billingData?.meta || {}),
    [billingData, isFilterTransitioning]
  );

  // SCRUM-825: which letters have at least one matching patient in the
  // current filter/page/search combo, used to grey out the rest of the A-Z
  // nav bar below. `available_letters` lives inside `results` (a sibling of
  // `patients`, not of `meta`) - same unwrapping as patientsList above, so
  // this also reflects the currently active patient search, since
  // activeSearchTerm/activeSearchPatientId are part of this same query.
  const availableLetters = useMemo(() => {
    if (isFilterTransitioning) return null;
    const results = billingData?.results || billingData || {};
    return results?.available_letters ?? null;
  }, [billingData, isFilterTransitioning]);

  // Footer page-number box (Figma still just shows the number) doubles as a
  // jump-to-page input - keep it in its own state so mid-typing digits
  // aren't clobbered by re-renders, and resync it from the server's
  // current_page whenever that changes from elsewhere (Back/Next, A-Z jump).
  const [pageInputValue, setPageInputValue] = useState(String(meta?.current_page || 1));
  useEffect(() => {
    setPageInputValue(String(meta?.current_page || 1));
  }, [meta?.current_page]);

  // SCRUM-683: A-Z nav - clicking a letter jumps straight to the page
  // containing the first last-name match; per the 2026-09-02 product
  // answer, this resets pagination (the backend picks the destination page,
  // not `page`) and takes priority over any other in-flight page nav.
  const handleAlphabetLetterClick = letter => {
    setJumpToLetter(letter);
    setActiveLetter(letter);
  };

  // Once a jump-triggered page loads, find the row the backend flagged
  // with "highlight": true and scroll/flash it into view - the visual cue
  // that this is the patient the user jumped to (see the API spec message:
  // "you need to hover over to that patient which have highlight: true").
  // Bug fix (SCRUM-829): jumpToLetter stays set (by design - see its own
  // comment above) until the user pages away or changes a filter, so this
  // effect used to also re-fire off a status-update mutation's in-place
  // cache patch (a new patientsList reference, same page). That resurfaced
  // the same highlight/scroll after the user had already scrolled away and
  // changed a *different* patient's billing status, yanking them back.
  // Track which letter's jump has already been consumed so a cache patch
  // for the same still-active letter is a no-op; a genuinely new letter
  // click resets this and re-triggers the scroll as before.
  //
  // Bug fix: clicking a second, different letter right after the first
  // (e.g. "S" then "B") re-ran this effect the instant jumpToLetter changed
  // to "B" - but keepPreviousData means patientsList was still showing S's
  // rows at that exact moment (B's fetch hadn't resolved yet). It matched
  // S's stale highlight row, scrolled back to it, and marked "B" as already
  // consumed against that stale data - so once B's real page actually
  // loaded, the effect saw jumpToLetter already "consumed" and silently
  // skipped it, and the user never got jumped to B at all. Only consume a
  // letter once its own fetch has actually settled (isBillingSummaryFetching
  // false), so patientsList is guaranteed to be that letter's real data.
  const consumedJumpToLetterRef = useRef(null);
  useEffect(() => {
    if (!jumpToLetter) {
      consumedJumpToLetterRef.current = null;
      return;
    }
    if (isBillingSummaryFetching) return;
    if (consumedJumpToLetterRef.current === jumpToLetter) return;
    const target = patientsList.find(p => p?.highlight);
    if (target?.patient_id != null) {
      setHighlightedPatientId(target.patient_id);
      consumedJumpToLetterRef.current = jumpToLetter;
    }
  }, [billingData, jumpToLetter, patientsList, isBillingSummaryFetching]);

  // SCRUM-687: "Back to Billing" - once the restored page's data loads,
  // scroll/flash the row the user originally clicked into the chart from
  // (same mechanism as the A-Z jump's highlight above). page/month/year/
  // patient_status are already restored from the URL before this ever
  // fires (see the useState initializers above), so by the time
  // patientsList is non-empty it should already contain this patient -
  // find() simply no-ops (leaving the ref set) if it doesn't.
  // Confirmed via debug logging 2026-09-04: it currently *doesn't* -
  // GET .../billing-summary/.../?page=1&page_size=50&patient_status=active
  // (identical params, no filters changed in between) returned two
  // completely non-overlapping sets of 50 patients for "page 1" on two
  // consecutive requests. That's a backend pagination bug (no stable sort
  // key - "page 1" isn't deterministic), not something fixable here; this
  // effect's silent no-op is the correct frontend behavior regardless -
  // land on the right page number (that part is already correct) and just
  // skip the highlight when the specific patient isn't on it.
  useEffect(() => {
    const target = pendingScrollToPatientId.current;
    if (!target || patientsList.length === 0) return;
    const match = patientsList.find(
      p => String(p?.patient_id) === String(target)
    );
    if (match) {
      setHighlightedPatientId(match.patient_id);
      pendingScrollToPatientId.current = null;
    }
  }, [patientsList]);

  useEffect(() => {
    if (highlightedPatientId == null) return;
    const el = document.getElementsByClassName(styles.highlightedRow)[0];
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => setHighlightedPatientId(null), 4000);
    return () => clearTimeout(timer);
  }, [highlightedPatientId]);

  // Handle a page-number/Back/Next click: update the active page, and
  // immediately (imperatively, right at click time) kick off a background
  // prefetch of the *next* page - not reactively off the clicked page's own
  // data resolving. This is deliberate: whether the clicked page's data
  // comes back via a fresh network fetch or an already-warm cache doesn't
  // matter here, so the N+1 chain advances on every single click,
  // deterministically, all the way to the last page. total_pages is stable
  // across pages for the same filter/pageSize combo (computed whole-tenant
  // server-side), so the currently-loaded meta is a valid source for it.
  const handlePageChange = newPage => {
    setPage(newPage);
    // SCRUM-683: an explicit page-number/Back/Next click is normal
    // pagination from here on - drop the A-Z letter so the next fetch uses
    // `page` again instead of re-resolving from the letter, and un-bold it
    // (the user has now manually moved off whatever page it landed on).
    setJumpToLetter(null);
    setActiveLetter(null);

    const totalPages = meta?.total_pages;
    if (totalPages && newPage + 1 <= totalPages) {
      queryClient.prefetchQuery(
        ["case-manager-billing-summary", summaryDate, billingType, patientStatus, newPage + 1, pageSize, billingStatus],
        () =>
          getSummary(
            summaryDate,
            billingType,
            locationId,
            undefined,
            undefined,
            patientStatus,
            newPage + 1,
            true,
            pageSize,
            undefined,
            undefined,
            undefined,
            billingStatus
          ),
        { staleTime: 30 * 1000 }
      );
    }
  };

  const handlePageInputChange = e => {
    setPageInputValue(e.target.value.replace(/[^0-9]/g, ""));
  };

  const commitPageInput = () => {
    const currentPage = meta?.current_page || 1;
    const totalPages = meta?.total_pages || 1;
    const parsed = Number.parseInt(pageInputValue, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setPageInputValue(String(currentPage));
      return;
    }
    const clamped = Math.min(parsed, totalPages);
    setPageInputValue(String(clamped));
    if (clamped !== currentPage) {
      handlePageChange(clamped);
    }
  };

  const handlePageInputKeyDown = e => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  // State to track location lock status directly from API response
  const isLocationLocked = useMemo(() => {
    return billingSummary?.location_billing_lock || false;
  }, [billingSummary]);

  const billingLockToggleMutation = useMutation(
    () => lockBillingToggle(locationId),
    {
      onSuccess: data => {
        // Manually patch every cached page (page/pageSize-suffixed) under
        // the current filter combo - covers both the active page and any
        // prefetched N+1 entry, without needing an exact key match.
        queryClient.setQueriesData(
          {
            predicate: query =>
              query.queryKey[0] === "case-manager-billing-summary" &&
              query.queryKey[1] === summaryDate &&
              query.queryKey[2] === billingType &&
              query.queryKey[3] === patientStatus
          },
          oldData => {
            if (!oldData) return oldData;
            // Bug fix: this used to only patch location_billing_lock, so the
            // Locked/Unlocked pill (driven by isLocationLocked) flipped
            // instantly but each CPT pill's own disabled state (driven by
            // the per-row `billing_locked` field - see isDisabled below)
            // stayed stale until invalidateQueries' background refetch
            // landed, which could take a moment - patch every row's
            // billing_locked here too so both update together.
            const patchPatients = patients =>
              Array.isArray(patients)
                ? patients.map(p => ({ ...p, billing_locked: data.billing_locked }))
                : patients;
            if (oldData.results) {
              return {
                ...oldData,
                results: {
                  ...oldData.results,
                  location_billing_lock: data.billing_locked,
                  patients: patchPatients(oldData.results.patients)
                }
              };
            }
            return {
              ...oldData,
              location_billing_lock: data.billing_locked,
              patients: patchPatients(oldData.patients)
            };
          }
        );

        queryClient.invalidateQueries(["case-manager-billing-summary"]);
        toast.success(data.message);
      },
      onError: () => {
        toast.error("Failed to toggle billing lock");
      }
    }
  );

  // Note: When filters or page/pageSize change, the query key changes and
  // React Query fetches the new combination automatically.

  const downloadSummaryQuery = useQuery(
    ["case-manager-billing-summary-download", summaryDate, billingType, locationId, patientStatus],
    () =>
      getSummary(
        summaryDate,
        billingType,
        locationId,
        undefined, // download (undefined to avoid backend CSV trigger)
        undefined, // summary
        patientStatus,
        1,
        false // pagination=false to fetch ALL records
      ),
    {
      enabled: false,
      cacheTime: 0,
      staleTime: 0,
      onSuccess: data => {
        // Parse the full JSON response to generate CSV client-side
        const fullPatientList = data?.results?.patients || data?.patients || [];

        if (fullPatientList.length === 0) {
          toast.error("No data to export");
          return;
        }

        const getCodeStatus = (billings, code) => {
          // Filter out non_billable statuses
          const relevantBillings = billings?.filter(b => b.code === code && b.status !== 'non_billable') || [];
          if (relevantBillings.length === 0) return "-";
          return relevantBillings.map(bill => `${bill.status}${bill.units > 1 ? ` x${bill.units}` : ""}`).join("; ");
        };

        let headers = [];
        if (billingType === "rpm") {
          headers = ["Patient Name", "DOB", "Place of Service / Provider", "ICD-10", "Consent Date", "DOS", "99453", "99445", "99454", "99470", "99457", "99458"];
        } else if (billingType === "ccm") {
          headers = ["Patient Name", "DOB", "Place of Service / Provider", "ICD-10", "Consent Date", "G0506", "99490", "99439", "99487", "99489"];
        } else if (billingType === "all") {
          headers = ["Patient Name", "DOB", "Place of Service / Provider", "ICD-10 (RPM)", "Consent Date (RPM)", "DOS", "99453", "99445", "99454", "99470", "99457", "99458", "ICD-10 (CCM)", "Consent Date (CCM)", "G0506", "99490", "99439", "99487", "99489"];
        }

        const csvRows = [headers.join(",")];

        fullPatientList.forEach(p => {
          let row = [];
          if (billingType === "rpm") {
            const consentDate = p.RPM_setup_date || p.created_at;
            const targetCodes = ["99445", "99454"];
            const relevantBillings = p.rpm_billings?.filter(b => targetCodes.includes(b.code)) || [];
            let dosString = "--";

            // Logic to append code to date in CSV
            if (relevantBillings.length > 0) {
              const entries = [];
              relevantBillings.forEach(bill => {
                if (bill.dates_of_service && bill.dates_of_service.length > 0) {
                  bill.dates_of_service.forEach(ds => {
                    // Format from YYYY-MM-DD to MM/DD/YYYY for CSV
                    const [y, m, d] = ds.split('-');
                    const formatted = `${m}/${d}/${y}`;
                    entries.push(`${formatted} (${bill.code})`);
                  });
                }
              });
              if (entries.length > 0) dosString = entries.join("; ");
            } else {
              const oldDate = convertUTCToLocal(p.code_stats?.[0]?.[99454]?.created_at);
              if (oldDate) dosString = oldDate;
            }

            row = [
              `"${formatLastFirstName(nameFieldsOf(p))}"`,
              formatUSADate(p.dob),
              `"${placeOfServiceName(p)} / ${p.provider || "--"}"`,
              `"${(p.rpm_codes || []).join("; ")}"`,
              formatUSADate(consentDate),
              `"${dosString}"`,
              getCodeStatus(p.rpm_billings, "99453"),
              getCodeStatus(p.rpm_billings, "99445"),
              getCodeStatus(p.rpm_billings, "99454"),
              getCodeStatus(p.rpm_billings, "99470"),
              getCodeStatus(p.rpm_billings, "99457"),
              getCodeStatus(p.rpm_billings, "99458")
            ];
          } else if (billingType === "ccm") {
            const consentDate = p.created_at;
            row = [
              `"${formatLastFirstName(nameFieldsOf(p))}"`,
              formatUSADate(p.dob),
              `"${placeOfServiceName(p)} / ${p.provider || "--"}"`,
              `"${(p.icd_codes || []).join("; ")}"`,
              formatUSADate(consentDate),
              getCodeStatus(p.ccm_billings, "G0506"),
              getCodeStatus(p.ccm_billings, "99490"),
              getCodeStatus(p.ccm_billings, "99439"),
              getCodeStatus(p.ccm_billings, "99487"),
              getCodeStatus(p.ccm_billings, "99489")
            ];
          } else if (billingType === "all") {
            const rpmConsentDate = p.RPM_setup_date || p.created_at;
            const ccmConsentDate = p.created_at;
            const targetCodes = ["99445", "99454"];
            const relevantBillings = p.rpm_billings?.filter(b => targetCodes.includes(b.code)) || [];
            let dosString = "--";

            // Logic to append code to date in CSV
            if (relevantBillings.length > 0) {
              const entries = [];
              relevantBillings.forEach(bill => {
                if (bill.dates_of_service && bill.dates_of_service.length > 0) {
                  bill.dates_of_service.forEach(ds => {
                    const [y, m, d] = ds.split('-');
                    const formatted = `${m}/${d}/${y}`;
                    entries.push(`${formatted} (${bill.code})`);
                  });
                }
              });
              if (entries.length > 0) dosString = entries.join("; ");
            } else {
              const oldDate = convertUTCToLocal(p.code_stats?.[0]?.[99454]?.created_at);
              if (oldDate) dosString = oldDate;
            }

            row = [
              `"${formatLastFirstName(nameFieldsOf(p))}"`,
              formatUSADate(p.dob),
              `"${placeOfServiceName(p)} / ${p.provider || "--"}"`,
              `"${(p.rpm_codes || []).join("; ")}"`,
              formatUSADate(rpmConsentDate),
              `"${dosString}"`,
              getCodeStatus(p.rpm_billings, "99453"),
              getCodeStatus(p.rpm_billings, "99445"),
              getCodeStatus(p.rpm_billings, "99454"),
              getCodeStatus(p.rpm_billings, "99470"),
              getCodeStatus(p.rpm_billings, "99457"),
              getCodeStatus(p.rpm_billings, "99458"),
              `"${(p.icd_codes || []).join("; ")}"`,
              formatUSADate(ccmConsentDate),
              getCodeStatus(p.ccm_billings, "G0506"),
              getCodeStatus(p.ccm_billings, "99490"),
              getCodeStatus(p.ccm_billings, "99439"),
              getCodeStatus(p.ccm_billings, "99487"),
              getCodeStatus(p.ccm_billings, "99489")
            ];
          }
          csvRows.push(row.join(","));
        });

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8" });
        saveAs(blob, buildReportFilename({
          year,
          month,
          doctype: DOCTYPES.BILLING,
          program: billingType === "all" ? ["RPM", "CCM"] : billingType,
          ext: "csv",
        }));
        toast.success("Report Downloaded Successfully");
      }
    }
  );

  // SCRUM-604: default list order is alphabetical by last name
  // (case-insensitive), with first name as a tiebreaker. Note this only
  // re-sorts the currently-loaded page - the backend still paginates in
  // first-name order, so this isn't globally correct across pages.
  const sortedPatients = useMemo(() => {
    if (!patientsList) return [];

    return [...patientsList].sort((a, b) =>
      compareByLastName(nameFieldsOf(a), nameFieldsOf(b))
    );
  }, [patientsList]);

  const { mutate: updateBillStatusMutation, isLoading: isBillStatusUpdating } =
    useMutation(
      ({ id, newStatus }) =>
        updateBillingStatus(id, locationId, {
          status: newStatus
        }),
      {
        onSuccess: (data, { previousStatus: clickedStatus }) => {
          const updatedBilling = data.billing;
          // SCRUM-898: the dollar cards move with the bill's own `amount`
          // (from the update-status response), using the same delta on every
          // cached page below.
          // One-time codes outside their last billable month leave the
          // dollar cards AND the status counts as they are.
          const skipTotals = skipsBillingAmount(updatedBilling, year, month);
          const amountDelta = skipTotals
            ? null
            : getBillingAmountDelta(
                clickedStatus,
                updatedBilling?.status,
                Number(updatedBilling?.amount) || 0
              );

          // 1. Manually patch every cached page (page/pageSize-suffixed)
          // under the current filter combo - covers both the active page
          // and any prefetched N+1 entry, without needing an exact key
          // match against the now-longer query key.
          if (updatedBilling) {
            queryClient.setQueriesData(
              {
                predicate: query =>
                  query.queryKey[0] === "case-manager-billing-summary" &&
                  query.queryKey[1] === summaryDate &&
                  query.queryKey[2] === billingType &&
                  query.queryKey[3] === patientStatus
              },
              oldData => {
                if (!oldData) return oldData;

                const currentPatients = oldData.results?.patients || oldData.patients || [];

                // SCRUM-792: capture the bill's status *before* this patch
                // overwrites it, so the Status Count card (below) can move
                // it out of its old bucket and into the new one in this
                // same cache update - previously nothing here touched
                // status_summary at all, so the card stayed stale until a
                // full refetch.
                let previousStatus = null;
                const patchBill = bill => {
                  if (bill.id !== updatedBilling.id) return bill;
                  previousStatus = bill.status;
                  return {
                    ...bill,
                    status: updatedBilling.status,
                    last_status_changed_at: updatedBilling.last_status_changed_at,
                    last_status_changed_by: updatedBilling.last_status_changed_by,
                    last_billed_date: updatedBilling.last_billed_date,
                    last_billable_date: updatedBilling.last_billable_date,
                    enabled: updatedBilling.enabled
                  };
                };

                const newPatients = currentPatients.map(patient => {
                  if (patient.patient_id !== updatedBilling.patient_id) return patient;

                  return {
                    ...patient,
                    rpm_billings: (patient.rpm_billings || []).map(patchBill),
                    ccm_billings: (patient.ccm_billings || []).map(patchBill)
                  };
                });

                // SCRUM-898: totals are whole-tenant and identical on every
                // page, so every cached page of this view gets the same
                // update - including pages the bill isn't on (otherwise a
                // prefetched/visited page shows the old totals and they
                // appear to change while paging). The clicked status covers
                // those pages.
                const fromStatus = previousStatus || clickedStatus;
                const container = oldData.results || oldData;
                const oldSummary = container?.status_summary;
                let newSummary = oldSummary;
                if (
                  !skipTotals &&
                  oldSummary &&
                  fromStatus &&
                  fromStatus !== updatedBilling.status
                ) {
                  const oldKey = `total_${fromStatus}`;
                  const newKey = `total_${updatedBilling.status}`;
                  newSummary = { ...oldSummary };
                  if (typeof newSummary[oldKey] === "number") {
                    newSummary[oldKey] = Math.max(0, newSummary[oldKey] - 1);
                  }
                  if (typeof newSummary[newKey] === "number" || newKey in newSummary) {
                    newSummary[newKey] = (newSummary[newKey] || 0) + 1;
                  }
                }

                const amountTotals = amountDelta
                  ? {
                      billed_this_month: addAmount(
                        container?.billed_this_month,
                        amountDelta.billed
                      ),
                      total_billable: addAmount(
                        container?.total_billable,
                        amountDelta.billable
                      )
                    }
                  : {};

                if (oldData.results) {
                  return {
                    ...oldData,
                    results: {
                      ...oldData.results,
                      patients: newPatients,
                      status_summary: newSummary,
                      ...amountTotals
                    }
                  };
                }

                return {
                  ...oldData,
                  patients: newPatients,
                  status_summary: newSummary,
                  ...amountTotals
                };
              }
            );
          }

          // 2. Invalidate other views (different filters)
          // SCRUM-996: also every Status Count pill-filtered list (billing
          // status is the last key element, on prefetch keys too), including
          // the one on screen. The patch above can't move a bill between
          // filtered lists, so e.g. a bill changed Paid -> Billable stayed in
          // the cached Paid list and showed again on returning to that pill.
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              if (key[0] !== "case-manager-billing-summary") return false;

              const isCurrentView =
                key[1] === summaryDate &&
                key[2] === billingType &&
                key[3] === patientStatus;
              const isPillFiltered = !!key[key.length - 1];

              return !isCurrentView || isPillFiltered;
            }
          });

          setUpdateStatus({
            patientId: null,
            code: null,
            status: null,
            id: null
          });
          setUpdateStatusCode(null);
          toast.success("Status updated successfully");
        },
        onError: error => {
          setUpdateStatus({
            patientId: null,
            code: null,
            status: null,
            id: null
          });
          setUpdateStatusCode(null);
          toast.error(
            error?.response?.data?.detail || "Failed to update billing status"
          );
        }
      }
    );

  // SCRUM-634/635: "Mark as Denied"/"Mark as Appealed" popups apply a status
  // to several selected codes at once for one patient - reuses the same
  // update-status endpoint as the single-pill cycle above, called once per
  // selected code, rather than reusing that mutation directly (it's wired to
  // a single-code `updateStatus` closure and isn't a good fit for a bulk
  // action).
  const { mutate: bulkUpdateBillStatusMutation, isLoading: isBulkStatusUpdating } =
    useMutation(
      async ({ ids, status }) =>
        Promise.all(ids.map(id => updateBillingStatus(id, locationId, { status }))),
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            predicate: query => query.queryKey[0] === "case-manager-billing-summary"
          });
          toast.success(
            markStatusModal.mode === "denied"
              ? "Marked as Denied"
              : "Marked as Appealed"
          );
          setMarkStatusModal({ open: false, mode: null, row: null, selectedIds: [] });
        },
        onError: error => {
          toast.error(
            error?.response?.data?.detail || "Failed to update billing status"
          );
        }
      }
    );

  useEffect(() => {
    if (selectedViews.rpm && selectedViews.ccm) {
      setBillingType("all");
    } else if (selectedViews.ccm) {
      setBillingType("ccm");
    } else if (selectedViews.rpm) {
      setBillingType("rpm");
    }
  }, [selectedViews]);

  useEffect(() => {
    if (!updateStatus || updateStatus?.status === "non-billable") return;

    // Per direct request: clicking a code pill cycles the three
    // billing-progress statuses (billable -> billed -> paid -> billable
    // ...). Denied is only reachable via the row's "Mark as Denied" action
    // (see getMarkStatusItems/openMarkStatusModal) - a Denied pill is
    // excluded from triggering this effect at all (see the pill's onClick
    // below). Appealed is the one exception: clicking an Appealed pill
    // resolves it straight to Paid, then falls into the normal
    // billed/paid/billable cycle from there.
    let newStatus = null;
    switch (updateStatus?.status) {
      case "billable":
        newStatus = "billed";
        break;
      case "billed":
        newStatus = "paid";
        break;
      case "paid":
        newStatus = "billable";
        break;
      case "appealed":
        newStatus = "paid";
        break;
      default:
        newStatus = "non-billable";
    }
    setUpdateStatusCode(newStatus);
    if (updateStatus?.id && updateStatus?.patientId) {
      updateBillStatusMutation({
        id: updateStatus.id,
        newStatus,
        previousStatus: updateStatus.status
      });
    }
  }, [updateStatus]);

  const toggleView = view => {
    if (view === "ccm" && !selectedViews.rpm && selectedViews.ccm) {
      return;
    }
    if (view === "rpm" && !selectedViews.ccm && selectedViews.rpm) {
      return;
    }

    setSelectedViews(prev => ({
      ...prev,
      [view]: !prev[view]
    }));
  };

  const [currentServiceType, setCurrentServiceType] = useState(null);
  const [patient, setPatient] = useState(null);

  // Appeal Letter States
  const [showAppealLetterModal, setShowAppealLetterModal] = useState(false);
  const [showAppealEmailForm, setShowAppealEmailForm] = useState(false);
  const [selectedPatientForAppeal, setSelectedPatientForAppeal] = useState(null);
  const [appealLetterGenerated, setAppealLetterGenerated] = useState(false);
  const [lastAppealGeneratedDate, setLastAppealGeneratedDate] = useState(null);
  const [appealLetterData, setAppealLetterData] = useState(null);
  const [isSendingAppealEmail, setIsSendingAppealEmail] = useState(false);

  // Appeal Letter Functions
  const getDeniedCodes = (patient, programType) => {
    const billings =
      programType === "RPM" ? patient?.rpm_billings : patient?.ccm_billings;

    if (!billings) return [];

    return billings
      .filter((b) => b.status === "denied")
      .map((b) => b.code);
  };

  const handleOpenAppealLetterModal = async (patientData) => {
    const programType = billingType === "ccm" ? "CCM" : "RPM";
    setCurrentServiceType(programType);
    setShowAppealLetterModal(true);

    // Reset appeal letter state so previous patient's data doesn't leak
    setAppealLetterGenerated(false);
    setLastAppealGeneratedDate(null);
    setAppealLetterData(null);

    // Start with billing table data
    let enrichedPatientData = { ...patientData };

    // Fetch full patient details to get ICD-10 codes (not included in billing summary)
    try {
      const patientDetails = await getPatientInfo(patientData.patient_id);
      if (patientDetails) {
        // Merge ICD-10 codes from patient details into the billing data
        enrichedPatientData = {
          ...patientData,
          rpm_codes: patientDetails.rpm_codes || [],
          ccm_codes: patientDetails.ccm_codes || [],
          icd_codes: patientDetails.icd_codes || [],
        };
      }
    } catch (error) {
      console.error("Error fetching patient details for ICD-10 codes:", error);
      // Continue with billing data even if patient details fetch fails
    }

    setSelectedPatientForAppeal(enrichedPatientData);
  };

  // SCRUM-687: builds the current billing view as a URL (tab/month/year/
  // status/page/page size) so "Back to Billing" can restore it exactly.
  // scrollToPatientId additionally records which row to scroll back to
  // (see handlePatientNameClick and the pendingScrollToPatientId effect
  // below) - optional since this is also the shape used for the plain
  // "restore the view" case with no specific row to return to.
  const buildBillingBackUrl = scrollToPatientId => {
    const params = new URLSearchParams();
    params.set(
      "view",
      selectedViews.rpm && selectedViews.ccm ? "all" : selectedViews.ccm ? "ccm" : "rpm"
    );
    params.set("month", String(month));
    params.set("year", String(year));
    // SCRUM-822: "active" is the default - omitted rather than round-tripped
    // as a literal "active" value (restoring with no patient_status param
    // already falls back to "active" via the useState initializer above).
    if (patientStatus !== "active") params.set("patient_status", patientStatus);
    // SCRUM-987: the selected Status Count pill, if any.
    if (billingStatus) params.set("billing_status", billingStatus);
    // SCRUM-683/687: `meta.current_page` (not the local `page` state) is
    // the actual page being viewed - after an A-Z jump, the server picks
    // the destination page via jump_to_letter and `page` state is
    // deliberately left untouched (see handleAlphabetLetterClick/
    // handlePageChange) until the user pages normally from there. Reading
    // `page` here would capture whatever page was current *before* the
    // jump, not the one actually on screen.
    params.set("page", String(meta?.current_page || page));
    params.set("page_size", String(pageSize));
    if (scrollToPatientId != null) {
      params.set("scroll_to_patient_id", String(scrollToPatientId));
    }
    // SCRUM-683: restores which A-Z letter shows bolded - cosmetic only,
    // doesn't drive the fetch (see activeLetter's own comment for why not
    // jumpToLetter itself).
    if (activeLetter) {
      params.set("active_letter", activeLetter);
    }
    return `/billings?${params.toString()}`;
  };

  const handlePatientNameClick = row => {
    if (!row?.patient_id) return;
    const backUrl = buildBillingBackUrl(row.patient_id);
    sessionStorage.setItem("_back_page", "billing");
    sessionStorage.setItem("_back_url", backUrl);
    // CCM/RPM enrollment flags match the convention used elsewhere when
    // linking to a patient's chart (patientSettings.js, patientDetail.js).
    const hasRpm = row?.rpm_billings?.length > 0 || row?.rpm_codes?.length > 0;
    const hasCcm = row?.ccm_billings?.length > 0 || row?.ccm_codes?.length > 0;
    navigate(
      `/patient/details?patient_id=${row.patient_id}&CCM=${+hasCcm}&RPM=${+hasRpm}`,
      { state: { backURL: backUrl } }
    );
  };

  // SCRUM-634/635: derives a stable per-row list of {key, id} for the code
  // checkboxes. Keyed by program+index+code rather than the raw billing
  // `id` - that id isn't guaranteed unique across every entry in this list,
  // so using it directly as the checkbox identity meant any two entries
  // sharing an id (including several undefined ids) toggled together. The
  // real `id` is still what gets sent to the backend, resolved separately
  // at confirm time.
  // Per direct request: a code is only eligible to be marked Denied if it's
  // currently Billed or Appealed, and only eligible to be marked Appealed
  // if it's currently Denied - `mode` (the modal being opened) gates which
  // codes even show up as selectable, rather than listing every code on
  // the row regardless of its current status.
  const isEligibleForMarkStatus = (billing, mode) => {
    if (mode === "denied") return billing.status === "billed" || billing.status === "appealed";
    if (mode === "appealed") return billing.status === "denied";
    return true;
  };

  const getMarkStatusItems = (row, mode) => {
    const rpmItems = (row?.rpm_billings || [])
      .filter(b => rpmCodes.includes(b.code) && isEligibleForMarkStatus(b, mode))
      .map((b, i) => ({ key: `rpm-${i}-${b.code}`, id: b.id }));
    const ccmItems = (row?.ccm_billings || [])
      .filter(b => ccmCodes.includes(b.code) && isEligibleForMarkStatus(b, mode))
      .map((b, i) => ({ key: `ccm-${i}-${b.code}`, id: b.id }));
    return [...rpmItems, ...ccmItems];
  };

  // SCRUM-634/635: opens the Mark as Denied/Appealed popup, pre-selecting
  // every eligible RPM/CCM billing code for the row per the Figma design.
  const openMarkStatusModal = (mode, row) => {
    const selectableKeys = getMarkStatusItems(row, mode)
      .filter(item => item.id)
      .map(item => item.key);

    setMarkStatusModal({
      open: true,
      mode,
      row,
      selectedIds: selectableKeys
    });
  };

  const closeMarkStatusModal = () => {
    setMarkStatusModal({ open: false, mode: null, row: null, selectedIds: [] });
  };

  const toggleMarkStatusCode = itemKey => {
    setMarkStatusModal(prev => ({
      ...prev,
      selectedIds: prev.selectedIds.includes(itemKey)
        ? prev.selectedIds.filter(key => key !== itemKey)
        : [...prev.selectedIds, itemKey]
    }));
  };

  const confirmMarkStatus = () => {
    if (markStatusModal.selectedIds.length === 0) return;
    const ids = getMarkStatusItems(markStatusModal.row, markStatusModal.mode)
      .filter(item => markStatusModal.selectedIds.includes(item.key))
      .map(item => item.id)
      .filter(Boolean);
    if (ids.length === 0) return;
    bulkUpdateBillStatusMutation({ ids, status: markStatusModal.mode });
  };

  // SCRUM-633: row 3-dots menu action dispatch.
  const handleRowAction = (action, row) => {
    switch (action) {
      case "mark_denied":
        openMarkStatusModal("denied", row);
        break;
      case "mark_appealed":
        openMarkStatusModal("appealed", row);
        break;
      case "download_audit_report":
        setShowDownloadModal(true);
        setPatientId(row?.patient_id);
        setPatient(row);
        setSelectedReportType("");
        setAuditReportMonth(month);
        setAuditReportYear(year);
        break;
      case "appeal_letter":
        handleOpenAppealLetterModal(row);
        break;
      default:
        break;
    }
  };

  const handleCloseAppealLetterModal = () => {
    setShowAppealLetterModal(false);
    setSelectedPatientForAppeal(null);
  };

  const handleGenerateAppealLetter = () => {
    setShowAppealLetterModal(false);
    setShowAppealEmailForm(true);
  };

  const handleCloseAppealEmailForm = () => {
    setShowAppealEmailForm(false);
  };

  const handleSendAppealEmail = async (formData) => {
    setIsSendingAppealEmail(true);

    try {
      // @react-pdf/renderer eagerly decodes large unicode lookup tables at
      // import time; loaded dynamically here (only when a PDF is actually
      // generated) instead of at module scope, so simply opening the
      // Billing page doesn't pull in that work every time.
      const { pdf } = await import("@react-pdf/renderer");
      const { default: AppealLetterPDF } = await import(
        "../../components/pdf/appeal-letter-pdf/AppealLetterPDF"
      );

      const programType = currentServiceType;
      const billingPeriodDate = `${year}-${month}`;

      // Determine if patient is dual-enrolled (in both RPM and CCM) AND both tabs are selected
      const hasRpmEnrollment = selectedPatientForAppeal?.rpm_billings?.length > 0 || selectedPatientForAppeal?.rpm_codes?.length > 0;
      const hasCcmEnrollment = selectedPatientForAppeal?.ccm_billings?.length > 0 || (selectedPatientForAppeal?.ccm_codes?.length > 0 || selectedPatientForAppeal?.icd_codes?.length > 0);
      const isDualEnrolled = hasRpmEnrollment && hasCcmEnrollment && selectedViews.rpm && selectedViews.ccm;

      // Extract data from the comprehensive form structure
      const { payerInfo, claimInfo, denialInfo, serviceDetails, careMgmt, signatureData, patientInfo, deniedCodes } = formData;

      // Build full payer address
      const fullPayerAddress = [
        payerInfo?.payerAddress,
        [payerInfo?.payerCity, payerInfo?.payerState, payerInfo?.payerZip].filter(Boolean).join(", ")
      ].filter(Boolean).join("\n");

      // Generate PDF blob
      const pdfDoc = (
        <AppealLetterPDF
          letterDate={formData.letterDate}
          payerName={payerInfo?.payerName}
          payerAddress={fullPayerAddress}
          patientName={patientInfo?.patientName || formatLastFirstName(nameFieldsOf(selectedPatientForAppeal))}
          patientDOB={patientInfo?.patientDOB || selectedPatientForAppeal?.dob}
          memberId={claimInfo?.memberId}
          claimNumber={claimInfo?.claimNumber}
          dateOfService={billingPeriodDate}
          deniedCodes={deniedCodes?.map(c => c.code) || []}
          programType={programType}
          icdCodes={patientInfo?.icdCodes}
          denialReason={denialInfo?.denialReason}
          eobDate={denialInfo?.eobDate}
          patientAge={patientInfo?.patientAge || selectedPatientForAppeal?.age}
          patientGender={patientInfo?.patientGender || selectedPatientForAppeal?.gender}
          diagnoses={patientInfo?.icdCodes?.join(", ")}
          enrollmentDate={patientInfo?.enrollmentDate}
          consentDate={patientInfo?.consentDate}
          providerName={signatureData?.providerName}
          providerTitle={signatureData?.providerTitle}
          providerCredentials={signatureData?.credentials}
          providerNPI={signatureData?.npi}
          practiceName={signatureData?.practiceName}
          practicePhone={signatureData?.providerPhone}
          practiceEmail={signatureData?.providerEmail}
          practiceTaxId={signatureData?.practiceTaxId}
          serviceDetails={serviceDetails}
          careMgmt={careMgmt}
          letterBody={formData.letterBody}
          isDualEnrolled={isDualEnrolled}
        />
      );

      const pdfBlob = await pdf(pdfDoc).toBlob();

      // Send email using the existing shareAuditReport function
      await shareAuditReport(
        pdfBlob,
        formData.toEmail,
        selectedPatientForAppeal?.patient_id,
        billingPeriodDate,
        `Appeal Letter - ${selectedPatientForAppeal?.patient_name}`
      );

      // Update state
      const now = new Date().toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
      setLastAppealGeneratedDate(now);
      setAppealLetterGenerated(true);
      setAppealLetterData(formData);

      toast.success("Appeal letter sent successfully");
      setShowAppealEmailForm(false);
      setShowAppealLetterModal(true);
    } catch (error) {
      console.error("Error sending appeal email:", error);
      toast.error("Failed to send appeal letter. Please try again.");
    } finally {
      setIsSendingAppealEmail(false);
    }
  };

  const handleDownloadAppealLetter = async () => {
    if (!selectedPatientForAppeal || !appealLetterData) return;

    try {
      const { pdf } = await import("@react-pdf/renderer");
      const { default: AppealLetterPDF } = await import(
        "../../components/pdf/appeal-letter-pdf/AppealLetterPDF"
      );

      const programType = currentServiceType;
      const billingPeriodDate = `${year}-${month}`;

      // Determine if patient is dual-enrolled (in both RPM and CCM) AND both tabs are selected
      const hasRpmEnrollment = selectedPatientForAppeal?.rpm_billings?.length > 0 || selectedPatientForAppeal?.rpm_codes?.length > 0;
      const hasCcmEnrollment = selectedPatientForAppeal?.ccm_billings?.length > 0 || (selectedPatientForAppeal?.ccm_codes?.length > 0 || selectedPatientForAppeal?.icd_codes?.length > 0);
      const isDualEnrolled = hasRpmEnrollment && hasCcmEnrollment && selectedViews.rpm && selectedViews.ccm;

      // Extract data from the comprehensive form structure
      const { payerInfo, claimInfo, denialInfo, serviceDetails, careMgmt, signatureData, patientInfo, deniedCodes } = appealLetterData;

      // Build full payer address
      const fullPayerAddress = [
        payerInfo?.payerAddress,
        [payerInfo?.payerCity, payerInfo?.payerState, payerInfo?.payerZip].filter(Boolean).join(", ")
      ].filter(Boolean).join("\n");

      const pdfDoc = (
        <AppealLetterPDF
          letterDate={appealLetterData.letterDate}
          payerName={payerInfo?.payerName}
          payerAddress={fullPayerAddress}
          patientName={patientInfo?.patientName || formatLastFirstName(nameFieldsOf(selectedPatientForAppeal))}
          patientDOB={patientInfo?.patientDOB || selectedPatientForAppeal?.dob}
          memberId={claimInfo?.memberId}
          claimNumber={claimInfo?.claimNumber}
          dateOfService={billingPeriodDate}
          deniedCodes={deniedCodes?.map(c => c.code) || []}
          programType={programType}
          icdCodes={patientInfo?.icdCodes}
          denialReason={denialInfo?.denialReason}
          eobDate={denialInfo?.eobDate}
          patientAge={patientInfo?.patientAge || selectedPatientForAppeal?.age}
          patientGender={patientInfo?.patientGender || selectedPatientForAppeal?.gender}
          diagnoses={patientInfo?.icdCodes?.join(", ")}
          enrollmentDate={patientInfo?.enrollmentDate}
          consentDate={patientInfo?.consentDate}
          providerName={signatureData?.providerName}
          providerTitle={signatureData?.providerTitle}
          providerCredentials={signatureData?.credentials}
          providerNPI={signatureData?.npi}
          practiceName={signatureData?.practiceName}
          practicePhone={signatureData?.providerPhone}
          practiceEmail={signatureData?.providerEmail}
          practiceTaxId={signatureData?.practiceTaxId}
          serviceDetails={serviceDetails}
          careMgmt={careMgmt}
          letterBody={appealLetterData.letterBody}
          isDualEnrolled={isDualEnrolled}
        />
      );

      const pdfBlob = await pdf(pdfDoc).toBlob();
      const fileName = `${programType}_Appeal_Letter_${selectedPatientForAppeal?.patient_name?.replace(
        /\s+/g,
        "_"
      )}.pdf`;

      // Save Appeal Letter to patient documents before downloading
      try {
        const pdfFile = new File([pdfBlob], fileName, { type: "application/pdf" });
        const formData = new FormData();
        formData.append("documents", pdfFile);
        formData.append("document_type_0", "APPEAL");

        await uploadPatientDocumentsWithId(formData, selectedPatientForAppeal?.patient_id);
        toast.success("Appeal letter saved to patient documents");
      } catch (uploadError) {
        console.error("Error saving appeal letter to patient documents:", uploadError);
        toast.error("Failed to save to patient documents, but download will proceed");
      }

      // Proceed with download
      saveAs(pdfBlob, fileName);
      toast.success("Appeal letter downloaded successfully");
    } catch (error) {
      console.error("Error downloading appeal letter:", error);
      toast.error("Failed to download appeal letter. Please try again.");
    }
  };

  const handleStartDateChange = value => {
    setDate(value);
  };

  const handleCloseModal = () => {
    setShowDownloadModal(false);
  };

  const handleDownloadPDF = () => {
    const auditReportDate = `${auditReportYear}-${auditReportMonth}`;

    let type = billingType;
    if (billingType === "all") {
      if (!selectedReportType) {
        toast.error("Please select a report type");
        return;
      }
      type = selectedReportType;
    }

    if (type === "rpm") {
      const url = `/billings/audit-report?date=${auditReportDate}&patientId=${patientId}&auditType=rpm`;
      window.open(url, "_blank");
    } else if (type === "ccm") {
      const url = `/patient/care-plan-pdf?patientId=${patientId}&month=${auditReportMonth}&year=${auditReportYear}`;
      window.open(url, "_blank");
    }
  };

  const handleSharePDF = () => {
    handleCloseModal();
    setIsSharingModalOpen(true);
  };

  const handleShare = (format, provider) => {
    setSharing(true);
    const iframe = document.createElement("iframe");
    // iframe.id = "audit-report-iframe";
    iframe.style.width = "100vw";
    iframe.style.height = "1px";
    iframe.style.position = "absolute";
    iframe.style.zIndex = "-10";
    iframe.style.opacity = "0";
    document.body.appendChild(iframe);

    let buttonInterval;
    iframe.onload = () => {
      buttonInterval = setInterval(() => {
        const downloadButton =
          iframe.contentDocument.getElementById("download-btn");

        if (downloadButton) {
          clearInterval(buttonInterval);
          downloadButton.click();
        }
      }, 200);
    };

    const date = `${auditReportYear}-${auditReportMonth}`;

    iframe.src = `/billings/share-audit-report?date=${date}&patientId=${patientId}&email=${provider}`;

    setTimeout(() => {
      clearInterval(buttonInterval);

      // toast.dismiss("audit-report");
      setSharing(false);
      toast.success("Audit Report Shared");
      setIsSharingModalOpen(false);
    }, 8000);

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 150000);
  };

  // A genuinely long-term Inactive patient (deactivated, but not this
  // month - see the Name column's tag logic below) has no active billing
  // window at all, so an empty cell for them means "not applicable," not
  // "no value yet." "--" stays the placeholder everywhere else.
  const isPatientInactiveRow = row =>
    patientStatus === "inactive" ||
    Boolean(row?.deactivated_at && !row?.deactivated_this_month);
  const emptyCellValue = row => (isPatientInactiveRow(row) ? "N/A" : "--");

  const columns = useMemo(() => {
    // SCRUM-630: redesigned column set (Name / Facility+Provider / Insurance /
    // ICD-10 / DOS / CPT Codes per program), verified directly against the
    // Figma table header row rather than the ticket text - see
    // feedback_figma_over_jira memory. DOB and the Days/Anchor Date/Setup
    // Date/Consent/monitoring-Time/Disenrollment columns (and the
    // SCRUM-690 Kalioundji-specific column branching that only applied to
    // this section) are dropped since none of them appear in the design.
    const commonColumns = [
      {
        key: "patient_name",
        label: "Name",
        alignLeft: true,
        // SCRUM-604: display-only "Last, First" in Title Case, sorted (and
        // header-click toggled) on last name rather than the raw stored string.
        // SCRUM-687: name links to the patient's chart, preserving the exact
        // billing view (month/page/filters) to return to via "Back to Billing".
        // Per Devon Gaur's comment on the ticket (2026-08-28): deactivated/
        // inactive patients have no accessible chart to link to (reactivating
        // them just to view it isn't a real option), so the hyperlink is
        // active-patients-only - plain text otherwise. patientStatus is the
        // page-level Active/Inactive Patients filter, so every row on the
        // page shares the same active/inactive state.
        // DOB stacked under the name per Figma node 5316:3150 (name, then
        // DOB below in the same cell). SCRUM-692's "Billing" status pill
        // (blue per Devon Gaur's comment) stacks above the name when the
        // row has one, per Figma node 5178:1100's "Zara Mitchell"/"Tara
        // Benson" rows.
        render: row => {
          // Bug fix: an "All Patients" option (patientStatus === "all") was
          // added to the filter alongside "active"/"inactive" (see the
          // Select below), but this check still only ever matched the
          // literal string "active" - so a deactivated-this-month patient
          // got no tag at all while viewing "All". deactivated_this_month
          // is a per-row fact from the API regardless of which patients the
          // page-level filter includes, so it should apply whenever the
          // view isn't exclusively the Inactive bucket (where, per
          // SCRUM-684, deactivated-this-month patients don't appear at all
          // - only genuinely long-inactive ones do).
          const isDeactivatedThisMonth =
            patientStatus !== "inactive" && row?.deactivated_this_month;
          // Bug fix: viewing "All Patients" and landing on a long-term
          // Inactive patient (e.g. via search) previously showed no tag at
          // all - isInactiveView only ever looked at the page-level filter,
          // which is a safe proxy while that filter is literally "inactive"
          // (every row it returns is inactive by definition) but says
          // nothing about any one row's actual status under "all". A row
          // that was deactivated but NOT this month is long-term Inactive
          // regardless of which filter surfaced it.
          const isInactiveView = isPatientInactiveRow(row);
          const tagLabel = isInactiveView ? "Inactive" : "Deactivated";

          // SCRUM-684: hover popover for the Deactivated (this month) /
          // Inactive (prior month) tag - now that deactivated_at/
          // deactivation_reason ship on the row (previously blocked on
          // backend, SCRUM-728), matching the existing SCRUM-632 Billable
          // metadata popover pattern rather than a plain static badge.
          // deactivation_reason values come back as short internal codes
          // ("ERROR", "no ins", "NOT USING", "DOUBLE", ...) - shown as-is
          // for now; flagged back to backend as something worth normalizing.
          // Matches Figma node 6556:1113 exactly: no card title (the pill
          // itself IS the label) - each field is its own stacked
          // bold-label-then-value block, not a label/value row.
          const deactivationPopover = (
            <div className={styles.deactivationPopoverCard}>
              <div className={styles.deactivationPopoverField}>
                <span className={styles.deactivationPopoverLabel}>
                  Date of Deactivation:
                </span>
                <span className={styles.deactivationPopoverValue}>
                  {displayDate(row?.deactivated_at) || "--"}
                </span>
              </div>
              <div className={styles.deactivationPopoverField}>
                <span className={styles.deactivationPopoverLabel}>Reason:</span>
                <span className={styles.deactivationPopoverValue}>
                  {row?.deactivation_reason || "--"}
                </span>
              </div>
            </div>
          );

          const deactivationTag = (isDeactivatedThisMonth || isInactiveView) && (
            <Tooltip
              content={deactivationPopover}
              // Bug fix: "top" centers the popup over the trigger
              // (translate(-50%)), which for the Name column - the very
              // first/leftmost column in the table - pushed roughly half
              // the popup off the left edge of the screen, permanently
              // invisible. Opening to the right instead (same as the
              // Billing status pill's own popover just below) keeps it
              // fully on-screen regardless of scroll position.
              position="right"
              customClass={styles.billInfoTooltip}
              arrow={false}
              tooltipStyle={{ zIndex: TOOLTIP_Z_INDEX }}
            >
              <span
                className={
                  isInactiveView ? styles.inactiveBadge : styles.deactivatedBadge
                }
              >
                {tagLabel}
                <span className={styles.pillDot} />
              </span>
            </Tooltip>
          );

          // SCRUM-692: "Billing" status pill + hover note, mirroring the
          // Deactivated pattern above ("this behaves consistently with the
          // Deactivated status pattern, where hovering ... displays the
          // ... reason"). Read-only for now - add/update needs a confirmed
          // write endpoint (see the StatusesModal.jsx pattern the ticket
          // points to, which is scoped to the session's current patient,
          // not an arbitrary row in this list) before that part can ship.
          // Per direct request: a styled card like the Deactivated popover
          // above (not the shared Tooltip's plain dark bubble), in the
          // Billing pill's own blue (#48A6C4, matching .billingStatusPill's
          // border) rather than Deactivated's gray, opening to the right of
          // the name/DOB instead of above it.
          const billingStatusPopover = row?.billing_status && (
            <div className={styles.billInfoCard} style={{ borderColor: "#3b82f6" }}>
              <p className={styles.billInfoHeader} style={{ color: "#3b82f6" }}>
                {capitalize(row.billing_status.status)}
              </p>
              <p className={styles.billInfoValue} style={{ textAlign: "left", whiteSpace: "normal" }}>
                {row.billing_status.description || "No description"}
              </p>
            </div>
          );

          const billingStatusPill = row?.billing_status && (
            <Tooltip
              content={billingStatusPopover}
              position="right"
              customClass={styles.billInfoTooltip}
              arrow={false}
              tooltipStyle={{ zIndex: TOOLTIP_Z_INDEX }}
            >
              <span className={styles.billingStatusPill}>
                {capitalize(row.billing_status.status)}
                <span className={styles.pillDot} />
              </span>
            </Tooltip>
          );

          // Per direct request: only a long-term Inactive patient's name is
          // non-clickable (their chart view isn't meant to be reached from
          // here) - Active and Deactivated-this-month both link through to
          // the patient's chart like normal.
          const nameNode = !isInactiveView ? (
            <span
              className={styles.patientNameLink}
              onClick={() => handlePatientNameClick(row)}
              role="link"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handlePatientNameClick(row);
                }
              }}
            >
              {formatLastFirstName(nameFieldsOf(row))}
            </span>
          ) : (
            <span>{formatLastFirstName(nameFieldsOf(row))}</span>
          );

          return (
            <div className={styles.nameCell}>
              {/* Figma node 5178:1584/5178:1587: status badges sit above
                  the name, not inline next to it. SCRUM-975: a deactivated/
                  inactive patient with a billing status shows both tags
                  side by side (deactivation first) - previously only the
                  deactivation tag rendered and the billing status was
                  hidden. */}
              {(deactivationTag || billingStatusPill) && (
                <div className={styles.nameCellTags}>
                  {deactivationTag}
                  {billingStatusPill}
                </div>
              )}
              <div className={styles.inactiveNameCell}>
                {nameNode}
              </div>
              <span className={styles.nameCellDob}>
                {displayDate(row?.dob) || emptyCellValue(row)}
              </span>
            </div>
          );
        },
        customSort: (a, b, direction) => {
          const cmp = compareByLastName(nameFieldsOf(a), nameFieldsOf(b));
          return direction === "asc" ? cmp : -cmp;
        }
      },
      {
        key: "facility_provider",
        label: "Place of Service / Provider",
        alignLeft: true,
        render: row => (
          <div className={styles.facilityProviderCell}>
            {/* `facility` carries the same name but is being removed */}
            <span>
              {row?.place_of_service?.name || row?.facility || emptyCellValue(row)}
            </span>
            <span>{row?.provider || emptyCellValue(row)}</span>
          </div>
        )
      },
      {
        key: "insurance",
        // No insurance field exists on the billing-summary response yet -
        // flagged to backend. Falls back to "--" (or "NA" for an Inactive
        // patient) until that field ships.
        label: "Insurance",
        alignLeft: true,
        render: row => row?.insurance?.name || row?.insurance_name || emptyCellValue(row)
      },
      {
        key: "icd_codes_grouped",
        label: "ICD-10",
        alignLeft: true,
        render: row => {
          const icdList = row?.icd_codes || [];
          if (icdList.length === 0) return emptyCellValue(row);
          // Backend doesn't split ICD-10 codes per program yet - flagged to
          // backend. Shows the same flat list once per active tab as a
          // placeholder until a per-program field exists.
          const groups = [];
          if (selectedViews.rpm) groups.push({ label: "RPM", codes: icdList });
          if (selectedViews.ccm) groups.push({ label: "CCM", codes: icdList });
          if (groups.length === 0) groups.push({ label: null, codes: icdList });

          return (
            <div className={styles.icdGroupedContainer}>
              {groups.map(g => (
                <div key={g.label || "all"} className={styles.icdGroupRow}>
                  {g.label && (
                    <span className={styles.icdGroupLabel}>{g.label} :</span>
                  )}
                  <div className={styles.icdCodeBadges}>
                    {g.codes.map((code, i) => (
                      <span key={i} className={styles.icdCodeBadge}>
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        }
      }
    ];

    // Renders one code's pill(s) for a row - same status-cycling/disabled/
    // tooltip logic as before SCRUM-630/631, only the pill's visual class and
    // displayed text changed (code + multiplier instead of the status word,
    // per the Figma CPT pill design).
    const renderCodeBadges = (row, code) => {
      const billings = [...(row?.ccm_billings || []), ...(row?.rpm_billings || [])];
      // Matches the PDF/CSV export path (renderStatusPill/getCodeStatus),
      // which already excludes non_billable - without this, a non_billable
      // entry rendered its own "NonBillable" pill right alongside a valid
      // billable/billed one for the same code instead of being dropped.
      const matchingBillings = billings.filter(
        bill => bill?.code === code && bill?.status !== "non_billable"
      );

      const renderSingleButton = billing => {
        const status = billing?.status;
        const id = billing?.id;
        const isEnabled = billing?.enabled !== false;
        const disabledMessage =
          "This billing code can only be finalized after the billing period ends (either next month or at the end of the next 30-day window).";

        const isUpdating =
          updateStatus.patientId === row?.patient_id &&
          (billing ? updateStatus.id === billing.id : updateStatus.code === code);

        const displayStatus = isUpdating ? updateStatusCode : status;
        const label = statusToLabelMap[displayStatus] || statusToLabelMap.default;
        const accentColor = statusToAccentColor[label] || statusToAccentColor.NonBillable;

        // SCRUM-632: row hover/info popover - Billable metadata card matching
        // the Figma design (status header + Last billable/billed/changed
        // at/changed by rows). Same underlying fields as before, just a real
        // popover instead of a native browser title tooltip. Per direct
        // request, the code-description summary line that used to sit
        // between the header and the date rows was removed.
        const billStatus = (
          <div
            className={`${styles.billInfoCard} ${styles.billInfoCardWide}`}
            style={{ borderColor: accentColor }}
          >
            <p className={styles.billInfoHeader} style={{ color: accentColor }}>
              {label}
            </p>
            <div className={styles.billInfoRow}>
              <span>Last billable</span>
              <span className={styles.billInfoValue}>
                {formatUSADateTimeDash(billing?.last_billable_date || billing?.last_billable)}
              </span>
            </div>
            <div className={styles.billInfoRow}>
              <span>Last billed</span>
              <span className={styles.billInfoValue}>
                {formatUSADateTimeDash(billing?.last_billed_date || billing?.last_billed)}
              </span>
            </div>
            <div className={styles.billInfoRow}>
              <span>Last changed at</span>
              <span className={styles.billInfoValue}>
                {formatUSADateTimeDash(billing?.last_status_changed_at)}
              </span>
            </div>
            <div className={styles.billInfoRow}>
              <span>Last changed by</span>
              <span className={styles.billInfoValue}>
                {billing?.last_status_changed_by || "N/A"}
              </span>
            </div>
          </div>
        );

        const show_unit = status === "billable" || status === "billed";

        const isDisabled =
          isBillingSummaryLoading || row.billing_locked || !isEnabled;

        const renderButton = () => (
          <Button
            disabled={isDisabled}
            className={`${styles.statusPill} ${styles[label?.toLowerCase()]} ${isDisabled ? styles.disabledBtn : ""
              }`}
            onClick={() => {
              // Denied only changes via the row's "Mark as Appealed" action,
              // not by clicking the pill directly - see the status-cycle
              // effect above. Appealed IS clickable: it resolves straight
              // to Paid, then joins the normal cycle.
              const isCyclable =
                label === "Billable" ||
                label === "Billed" ||
                label === "Paid" ||
                label === "Appealed";
              if (isCyclable && !isBillingSummaryFetching) {
                setUpdateStatus({
                  patientId: row?.patient_id,
                  code,
                  status: displayStatus,
                  id
                });
              } else if (label === "Denied" && !isBillingSummaryFetching) {
                toast("Use the ⋮ menu to mark this Denied code as Appealed.", {
                  icon: "ℹ️"
                });
              }
            }}
          >
            <span className={styles.pillDot} />
            {code}
            {show_unit && billing?.units > 1 && ` (x${billing.units})`}
            {isLocationLocked && label !== "NonBillable" && <FiLock className="ml-1" />}
          </Button>
        );

        return (
          <div key={id || "default"}>
            {(isBillStatusUpdating || isBillingSummaryFetching) && isUpdating ? (
              <button className={`${styles.statusPill} ${styles.loading}`}>
                <div className="spinner"></div>
              </button>
            ) : !isEnabled ? (
              <Tooltip
                content={disabledMessage}
                position="top"
                tooltipStyle={{ zIndex: TOOLTIP_Z_INDEX }}
              >
                <span style={{ cursor: "not-allowed", display: "inline-block" }}>
                  {renderButton()}
                </span>
              </Tooltip>
            ) : (
              <Tooltip
                content={billStatus}
                position="top"
                customClass={styles.billInfoTooltip}
                arrow={false}
                tooltipStyle={{ zIndex: TOOLTIP_Z_INDEX }}
              >
                {renderButton()}
              </Tooltip>
            )}
          </div>
        );
      };

      // No billing for this code at all (or the only entry was the
      // non_billable one just filtered out above) - render nothing, rather
      // than a placeholder pill that still displayed as a "NonBillable" pill
      // via the same status-label fallback the explicit non_billable status
      // used to hit.
      if (matchingBillings.length === 0) {
        return null;
      }

      return matchingBillings.map(billing => renderSingleButton(billing));
    };

    // Info-icon popup on the CPT Codes (RPM)/(CCM) column headers - lists
    // every billing code for that program (from the billing-settings
    // catalog, not just the ones this table happens to render pills for)
    // with its code + description, so users don't have to leave the
    // dashboard to look up what a code means.
    const cptCodesLogicContent = programLabel => {
      const codes = (billingCodesQuery?.data || []).filter(
        c => c?.category === programLabel
      );
      return (
        <div className={`${styles.billInfoCard} ${styles.cptLogicCard}`}>
          <p className={styles.cptLogicHeader}>{programLabel} CPT Codes Logic</p>
          <div className={styles.cptLogicList}>
            {codes.length === 0 ? (
              <p className={styles.cptLogicRow}>No codes found.</p>
            ) : (
              codes.map(c => (
                <div key={c.id} className={styles.cptLogicRow}>
                  <span className={styles.cptLogicCode}>{c.code}:</span>{" "}
                  {c.description}
                </div>
              ))
            )}
          </div>
        </div>
      );
    };

    const cptCodesColumnLabel = programLabel => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
        <Tooltip
          content={cptCodesLogicContent(programLabel)}
          position="bottom"
          customClass={styles.billInfoTooltip}
          arrow={false}
          tooltipStyle={{ zIndex: TOOLTIP_Z_INDEX }}
        >
          <FiInfo size={13} style={{ marginTop: "5px" }} />
        </Tooltip>
        {`CPT Codes (${programLabel})`}
      </span>
    );

    // SCRUM-630/631: one grouped column per program - a wrapping grid of
    // pills for every code in that program, matching the Figma "CPT Codes
    // (RPM)/(CCM)/(PCM)" columns (replaces the old one-column-per-code
    // layout; column header no longer needs the per-code info icon since
    // there's one column per program now, not per code).
    const renderCptCodesColumn = (label, programCodes) => ({
      key: `cpt_codes_${label}`,
      label: cptCodesColumnLabel(label),
      alignLeft: true,
      sorting: false,
      // SCRUM-684: "CPT codes are not displayed for patients in the
      // Inactive bucket" - per Devon Gaur's comment, the column itself
      // stays (same columns as the active view), just showing N/A instead
      // of pills.
      render: row =>
        patientStatus === "inactive" ? (
          "N/A"
        ) : (
          <div className={styles.cptCodesGroup}>
            {programCodes.map(code => (
              <React.Fragment key={code}>{renderCodeBadges(row, code)}</React.Fragment>
            ))}
          </div>
        )
    });

    // PCM billing is out of scope for now (no tab/data source) - the
    // CPT Codes (PCM) column is removed entirely rather than shown empty.
    const statusColumns = [
      ...(selectedViews.rpm ? [renderCptCodesColumn("RPM", rpmCodes)] : []),
      ...(selectedViews.ccm ? [renderCptCodesColumn("CCM", ccmCodes)] : [])
    ];

    // SCRUM-633: row 3-dots menu replaces the two bare icon buttons. Reuses
    // the existing Download Audit Report / Appeal Letter handlers as-is
    // (SCRUM-636) and adds Mark as Denied / Mark as Appealed (SCRUM-634/635).
    // "Update Insurance" appears in the Figma menu but is explicitly out of
    // scope per the epic and this ticket's own "per product direction" note.
    const actionsColumn = [
      {
        key: "actions",
        label: "",
        alignLeft: true,
        sorting: false,
        // SCRUM-627: Jira-style "edit columns" icon, sitting in the
        // Actions column's otherwise-empty header cell. customHeader is an
        // existing table.js hook (already used elsewhere for sortable
        // headers) - using it here means no changes to the shared Table
        // component at all.
        // The popup itself (checkboxes/Save/Reset) is rendered separately,
        // outside this memo entirely (see ColumnsMenuPopup in the main
        // return below) - it used to live right here, which meant every
        // piece of state it touched had to be re-added to this memo's own
        // dependency array or the popup would silently render stale/inert
        // (clicks updated state this memo never recomputed for). Keeping
        // only the trigger button in the memoized header avoids that
        // dependency entirely, since the popup no longer derives from a
        // memoized value at all.
        customHeader: () => (
          <button
            ref={columnsEditTriggerRef}
            type="button"
            onClick={e => {
              e.stopPropagation();
              openColumnsMenu();
            }}
            title="Choose columns"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              border: "none",
              background: "transparent",
              color: "#ffffff",
              cursor: "pointer"
            }}
          >
            <Edit2 size={18} style={{ flexShrink: 0 }} />
          </button>
        ),
        render: row => (
          <Dropdown
            align="right"
            usePortal
            className={styles.rowActionsMenu}
            onChange={value => handleRowAction(value, row)}
            trigger={
              <button
                type="button"
                className={styles.rowActionsTrigger}
                onClick={e => e.stopPropagation()}
                title="Actions"
              >
                <MoreVertical size={20} style={{ flexShrink: 0 }} />
              </button>
            }
          >
            <DropdownItem value="mark_denied" className={styles.rowActionsItem}>
              Mark as Denied
            </DropdownItem>
            <DropdownItem value="mark_appealed" className={styles.rowActionsItem}>
              Mark as Appealed
            </DropdownItem>
            <DropdownSeparator />
            <DropdownItem value="download_audit_report" className={styles.rowActionsItem}>
              Download Audit Report
            </DropdownItem>
            <DropdownItem value="appeal_letter" className={styles.rowActionsItem}>
              Appeal letter
            </DropdownItem>
          </Dropdown>
        )
      }
    ];

    // Restored pre-redesign columns (from before commit cf6bda62 / the
    // SCRUM-626 redesign) - these existed in the RPM/CCM billing tables
    // before the Figma redesign dropped them (the new design's ICD-10
    // grouped column already covers the old per-program rpm_codes/
    // icd_codes columns, so those specifically aren't restored - just the
    // program-level Days/Time/Setup/Anchor data that isn't represented
    // anywhere else in the new column set). Field names and render logic
    // match the pre-redesign code exactly.
    // SCRUM-688: Days/Setup Date/Anchor Date get the standardized info
    // icon + exact definition text specified in the ticket - the other
    // restored column (Time) isn't covered by that ticket and doesn't get
    // one.
    const infoIconLabel = (text, definition) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
        <Tooltip
          content={definition}
          position="top"
          tooltipStyle={{ zIndex: TOOLTIP_Z_INDEX }}
        >
          <FiInfo size={13} style={{ marginTop:'5px'}} />
        </Tooltip>
        {text}
      </span>
    );

    // Per direct request: "Consent" is a general column (available in the
    // picker regardless of which program tab is selected - see its
    // "generic" group in CONFIGURABLE_COLUMNS above), not CCM-only -
    // RPM_setup_date is RPM's own consent-equivalent date (same fallback
    // used for the RPM PDF export's consent date elsewhere on this page),
    // falling back to created_at for a CCM-only patient with no RPM setup
    // date at all.
    const consentColumn = {
      key: "ccm_consent_date",
      label: "Consent",
      alignLeft: true,
      render: row =>
        displayDateAsIs(row.RPM_setup_date || row.created_at) || emptyCellValue(row)
    };
    const ccmTimeColumn = {
      key: "ccm_monitoring_time",
      label: "CCM Time",
      alignLeft: true,
      render: row => {
        const formatted = formatSecondsToTime(row.ccm_monitoring_time);
        return formatted === "--" ? emptyCellValue(row) : formatted;
      }
    };
    const ccmRestoredColumns = selectedViews.ccm ? [ccmTimeColumn] : [];

    const rpmTimeColumn = {
      key: "rpm_monitoring_time",
      label: "RPM Time",
      alignLeft: true,
      render: row => {
        const formatted = formatSecondsToTime(row.rpm_monitoring_time);
        return formatted === "--" ? emptyCellValue(row) : formatted;
      }
    };
    const rpmDaysColumn = {
      key: "rolling_reading_days",
      label: infoIconLabel(
        "Days",
        <>
          Qualifying days of readings for the patient's 30-day billing
          window(s) that closed during this month.
          <br />
          <br />
          Days does not represent calendar-month reading days.
          <br />
          <br />
          If two billing windows close within the same month, both
          qualifying day counts are displayed separately.
        </>
      ),
      alignLeft: true,
      render: row => row.rolling_reading_days ?? 0
    };
    const rpmSetupColumn = {
      key: "rpm_setup_date",
      label: infoIconLabel("Setup Date", "Date of device issuance."),
      alignLeft: true,
      render: row => displayDateAsIs(row.RPM_setup_date)
    };
    const rpmAnchorColumn = {
      key: "rpm_anchor_date",
      label: infoIconLabel(
        "Anchor Date",
        "Start date of the patient's new 30-day billing window."
      ),
      alignLeft: true,
      render: row => {
        if (!row?.RPM_anchor_date) return emptyCellValue(row);
        const [y, m, d] = row.RPM_anchor_date.split("-");
        return `${m}-${d}-${y}`;
      }
    };
    const rpmRestoredColumns = selectedViews.rpm
      ? [rpmTimeColumn, rpmDaysColumn, rpmSetupColumn, rpmAnchorColumn]
      : [];

    const dosColumn = {
      key: "dos",
      label: "DOS",
      alignLeft: true,
      render: row => {
          // Logic to get DOS from 99445 and 99454 billings
          const targetCodes = ["99445", "99454"];
          const relevantBillings = row.rpm_billings?.filter(b => targetCodes.includes(b.code)) || [];

          if (relevantBillings.length > 0) {
            const entries = [];
            relevantBillings.forEach(bill => {
              if (bill.dates_of_service && bill.dates_of_service.length > 0) {
                bill.dates_of_service.forEach(dateStr => {
                  // Format YYYY-MM-DD to MM-DD-YYYY
                  const [y, m, d] = dateStr.split('-');
                  const formattedDate = `${m}-${d}-${y}`;
                  // Push object instead of string for styling
                  entries.push({ date: formattedDate, code: bill.code });
                });
              }
            });

            if (entries.length > 0) {
              // SCRUM-630: card per entry (date on top, code italicized
              // below), matching Figma node 3622:2148 - not the previous
              // inline "date (code)" text treatment.
              return (
                <div className={styles.dosEntryList}>
                  {entries.map((entry, idx) => (
                    <div key={idx} className={styles.dosEntryCard}>
                      <span className={styles.dosEntryDate}>{entry.date}</span>
                      <span className={styles.dosEntryCode}>{entry.code}</span>
                    </div>
                  ))}
                </div>
              );
            }
          }

          // Bug fix: convertUTCToLocal returns the literal string "--" (not
          // a falsy value) when there's no date, so `oldVal || emptyCellValue`
          // never actually fell through to "NA" for an Inactive patient -
          // "--" is truthy, so the `||` always short-circuited on it first.
          const oldVal = convertUTCToLocal(row.code_stats?.[0]?.[99454]?.created_at);
          return oldVal === "--" ? emptyCellValue(row) : oldVal;
        }
      };

    // SCRUM-627: drop any column the user has hidden via the header's
    // edit-columns picker (visibleColumns) - every entry in
    // CONFIGURABLE_COLUMNS goes through this, generic or program-specific
    // alike; patient_name/actions/CPT columns are never in that list, so
    // they're never affected by it.
    const isColumnHidden = key => visibleColumns[key] === false;
    // No dedicated filler column - the last visible CPT Codes column
    // itself absorbs the table's remaining width (see columnWidths below),
    // so Actions stays immediately after it, flush against the table's
    // true right edge instead of trailing behind an empty spacer column.
    const allColumns = [
      ...commonColumns,
      consentColumn,
      ...ccmRestoredColumns,
      ...rpmRestoredColumns,
      dosColumn,
      ...statusColumns,
      ...actionsColumn
    ];
    return allColumns.filter(col => !isColumnHidden(col.key));
  }, [
    selectedViews,
    updateStatus,
    updateStatusCode,
    isBillStatusUpdating,
    isBillingSummaryLoading,
    month, // Added to ensure buttons access current month state
    year, // Added to ensure buttons access current year state
    visibleColumns,
    columnsMenuOpen,
    columnsMenuPos,
    patientStatus,
    // SCRUM-687: the Name column's onClick closes over
    // handlePatientNameClick -> buildBillingBackUrl, which reads
    // `meta.current_page` (the actual page on screen, including after an
    // A-Z jump)/`page`/`pageSize` - without these, paging (Back/Next/a page
    // number/A-Z jump) never invalidated this memo, so the "Back to
    // Billing" URL kept whatever page was current the *last* time columns
    // happened to recompute instead of where the user actually was when
    // they clicked a name.
    page,
    pageSize,
    meta,
    activeLetter,
    // CPT Codes (RPM)/(CCM) header info-icon popup reads this - it starts
    // undefined and resolves after mount, so without it here the popup
    // would be frozen on "No codes found." forever once memoized before
    // the fetch completed.
    billingCodesQuery.data
  ]);

  // Floating horizontal scrollbar, mirroring the same approach already used
  // in the Billing PDF preview (src/components/pdf/billing-pdf/index.jsx):
  // with disableInternalScroll, the table's real horizontal scrollbar sits
  // on Table's own internal .split_body_scroll element, which - on a long
  // patient list - can be far below the fold, forcing a scroll all the way
  // down just to scroll sideways. This mirrors that element's scrollLeft
  // into a bar pinned to the bottom of the viewport instead, so it's always
  // reachable. Found via querySelector (Table isn't forwardRef) rather than
  // any change to the shared Table component, so no other page using Table
  // is affected.
  const tableWrapperRef = useRef(null);
  const bodyScrollElRef = useRef(null);
  const [scrollTrack, setScrollTrack] = useState({ left: 0, width: 0, contentWidth: 0 });
  const [bodyScrollLeft, setBodyScrollLeft] = useState(0);
  // .tableFooterBar (below) is itself position:sticky/bottom:0 - the
  // floating scrollbar needs to sit just above it rather than also pinning
  // to bottom:0, or the two would sit on top of each other once the user
  // scrolls far enough for the sticky footer to reach the viewport bottom.
  // Tracking the footer's own live top (not just a fixed viewport offset)
  // matters because the footer is sticky, not fixed: on a short list (e.g.
  // a single search result) it sits right under the table instead of
  // pinned to the viewport bottom, and a bar positioned via a static
  // `bottom: <footer height>` would float in the empty space below the
  // table instead of tracking it.
  const footerBarRef = useRef(null);
  const [footerTop, setFooterTop] = useState(0);

  useEffect(() => {
    const footerEl = footerBarRef.current;
    if (!footerEl) return;
    const measureFooter = () => setFooterTop(footerEl.getBoundingClientRect().top);
    measureFooter();
    const resizeObserver = new ResizeObserver(measureFooter);
    resizeObserver.observe(footerEl);
    window.addEventListener("scroll", measureFooter, { passive: true });
    window.addEventListener("resize", measureFooter);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", measureFooter);
      window.removeEventListener("resize", measureFooter);
    };
  }, [sortedPatients, columns]);

  // Custom-drawn track+thumb rather than exposing the real scroll
  // container's own native scrollbar (or a second native-scrollbar
  // container mirroring it) - two visible native scrollbars was exactly
  // the bug being fixed here, and native scrollbar appearance/hide support
  // (::-webkit-scrollbar, scrollbar-width) is inconsistent enough across
  // browsers/OS scrollbar themes that a plain hide-it CSS rule wasn't
  // reliably taking effect. This way there is exactly one bar, always,
  // regardless of platform. Table's real scroll container stays the actual
  // scroll owner (mouse wheel/trackpad still works on the table itself);
  // this thumb only reads its scroll position and, when dragged, writes
  // back to it directly.
  useEffect(() => {
    const bodyScrollEl = tableWrapperRef.current?.querySelector(
      `.${tableStyles.split_body_scroll}`
    );
    bodyScrollElRef.current = bodyScrollEl;
    if (!bodyScrollEl) return;

    const measure = () => {
      const rect = bodyScrollEl.getBoundingClientRect();
      setScrollTrack({
        left: rect.left,
        width: rect.width,
        contentWidth: bodyScrollEl.scrollWidth
      });
      setBodyScrollLeft(bodyScrollEl.scrollLeft);
    };
    measure();

    const handleBodyScroll = () => setBodyScrollLeft(bodyScrollEl.scrollLeft);
    bodyScrollEl.addEventListener("scroll", handleBodyScroll);
    window.addEventListener("resize", measure);

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(bodyScrollEl);

    return () => {
      bodyScrollEl.removeEventListener("scroll", handleBodyScroll);
      window.removeEventListener("resize", measure);
      resizeObserver.disconnect();
    };
  }, [sortedPatients, columns]);

  const maxScrollLeft = Math.max(0, scrollTrack.contentWidth - scrollTrack.width);
  const thumbWidth = Math.max(
    30,
    scrollTrack.contentWidth ? (scrollTrack.width / scrollTrack.contentWidth) * scrollTrack.width : 0
  );
  const thumbLeft =
    maxScrollLeft > 0 ? (bodyScrollLeft / maxScrollLeft) * (scrollTrack.width - thumbWidth) : 0;

  const handleThumbMouseDown = e => {
    e.preventDefault();
    const startX = e.clientX;
    const startScrollLeft = bodyScrollLeft;
    const trackSpan = scrollTrack.width - thumbWidth;

    const handleMouseMove = moveEvent => {
      if (!bodyScrollElRef.current || trackSpan <= 0) return;
      const deltaX = moveEvent.clientX - startX;
      const nextScrollLeft = Math.min(
        maxScrollLeft,
        Math.max(0, startScrollLeft + (deltaX / trackSpan) * maxScrollLeft)
      );
      bodyScrollElRef.current.scrollLeft = nextScrollLeft;
    };
    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  // Clicking the track itself (not the thumb) jumps toward that point.
  const handleTrackMouseDown = e => {
    if (e.target !== e.currentTarget || !bodyScrollElRef.current) return;
    const trackRect = e.currentTarget.getBoundingClientRect();
    const clickRatio = (e.clientX - trackRect.left) / trackRect.width;
    bodyScrollElRef.current.scrollLeft = clickRatio * maxScrollLeft;
  };

  const tableWrapperStyle = {
    overflowX: "auto",
    display: "block",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
  };

  const getHeadingTitle = () => {
    // if (selectedViews.ccm && !selectedViews.rpm) {
    //   return "CCM Billing";
    // } else if (selectedViews.rpm && !selectedViews.ccm) {
    //   return "RPM \billing";
    // } else if (selectedViews.ccm && selectedViews.rpm) {
    //   return "CCM & RPM Billings";
    // } else {
    //   return "Billings";
    // }
    return "Billings";
  };

  const datePickerRef = useOnClickOutside(() => setShowDatePicker(false));

  const downloadCSVReport = () => {
    // Trigger the refetch. The onSuccess handler in useQuery will handle the CSV generation and download.
    downloadSummaryQuery.refetch();
  };

  const statusCounts = useMemo(() => {
    const counts = {
      total: billingSummary?.patients?.length || 0,
      billable: 0,
      billed: 0,
      paid: 0,
      denied: 0,
      appealed: 0,
      deniedAfterAppeal: 0,
      non: 0
    };

    // Calculating "non-billable"
    let codeCount = 0;
    if (selectedViews.ccm && selectedViews.rpm) {
      codeCount = ccmCodes.length + rpmCodes.length;
    } else if (selectedViews.rpm) {
      codeCount = rpmCodes.length;
    } else if (selectedViews.ccm) {
      codeCount = ccmCodes.length;
    }

    // Processing each patient's billings
    // NOTE: This now only counts visible page items if billingSummary.patients is just one page
    // BUT the new API provides status_summary in billingSummary.status_summary

    return counts;
  }, [billingSummary, selectedViews]);

  // can size the number down to whatever still fits on one line.
  const renderMetricCard = (header, amount) => {
    const formatted = formatNumber("currency", amount ?? 0, {
      currency: "USD"
    });

    return (
      <div className={styles.metricCard}>
        <div className={styles.metricHeader}>{header}</div>
        <div
          className={styles.metricValue}
          style={{ "--metric-value-em": measureCurrencyEm(formatted) }}
        >
          {formatted}
        </div>
      </div>
    );
  };

  const renderStatusCounts = () => {
    // Use the API provided status summary if available
    const summary = billingSummary?.status_summary || {};

    // SCRUM-987: each pill filters the patient list to that status (a zero
    // count is still clickable). The counts come from status_summary, which
    // the backend never filters, so they stay the same whichever is selected.
    return (
      <div className={styles.statusCountCard}>
        <div className={styles.statusCountHeader}>
          <span>Status Count</span>
        </div>
        <div className={styles.statusCountRow}>
          {STATUS_COUNT_PILLS.map(pill => {
            const isSelected = billingStatus === pill.key;
            return (
              <button
                key={pill.key}
                type="button"
                aria-pressed={isSelected}
                title={
                  isSelected
                    ? "Show all patients"
                    : `Show only patients with ${pill.label} codes`
                }
                onClick={() => handleStatusPillClick(pill.key)}
                className={`${styles.statusCountItem} ${styles[`${pill.key}Count`]} ${
                  isSelected ? styles.statusCountItemSelected : ""
                }`}
              >
                <span className={styles.countLabelRow}>
                  <span className={styles.countDot} />
                  <span className={styles.countLabel}>{pill.label}</span>
                </span>
                <span className={styles.countValue}>
                  {summary?.[pill.summaryKey] ?? 0}
                </span>
              </button>
            );
          })}

          {/* <div className={styles.statusCountItem}>
            <span className={styles.countLabel}>Non-Billable</span>
            <span className={styles.countValue}>
              {billingSummary?.status_summary?.total_non_billable}
            </span>
          </div> */}
        </div>
      </div>
    );
  };

  // SCRUM-630/631: column widths start at the Figma "Ready" frame's exact
  // measurements (5316:2829 header row, x-distance between each
  // consecutive column header) - columnWidths sets an explicit width on
  // both the header-only and body-only <table> (table.js's
  // getCellWidthStyle), which is what keeps the two independently-laid-out
  // tables' columns aligned under splitHeaderScroll, so both need the
  // exact same numbers either way.
  // facility_provider/insurance are widened past Figma's raw measurement:
  // that number fit the *data* ("Dr Kalioundji 1"), but not the *header*
  // label itself ("Facility / Provider" / "Insurance" + sort icon) once
  // actually rendered - confirmed against a live screenshot where both
  // were truncating with an ellipsis. Both (and patient_name) still grow
  // further when real content on the page is wider than that - a bare
  // name/facility/insurance string has nowhere to wrap to, so it would
  // otherwise get clipped instead of the column taking the space it needs.
  // ICD-10/DOS stay fixed at the Figma number: ICD-10 is a pill grid that
  // already wraps safely to more rows via flex-wrap (verified - no
  // clipping risk), and DOS is a fixed "MM-DD-YYYY" format that never
  // varies in length, so neither has anything to grow for.
  // actions is the one deliberate Figma deviation: Figma's own measured
  // delta (55px) is narrower than the 35px button + this table's standard
  // 16px cell padding on both sides (67px minimum) - 55px would clip/touch
  // the button against its own cell edges in an actual browser render.
  const columnWidths = useMemo(() => {
    // Every width below is checked against its own header label at this
    // table's actual header font (14.4px semibold, ~7.9px/char) plus
    // whatever icon(s) render next to it (sort arrows on every sortable
    // column, plus the info icon on Days/Setup Date/Anchor Date) and the
    // standard 32px cell padding - not just Figma's number or a guess.
    const figmaColumnWidths = {
      patient_name: 111,
      // 220 measured close to exactly what "Facility / Provider" + its
      // sort icon need, with no real margin - widened slightly for
      // headroom rather than sitting right at the edge of truncating.
      // SCRUM-947: label is now "Place of Service / Provider", 8 chars
      // longer (~63px at 7.9px/char), so widened to match.
      facility_provider: 300,
      insurance: 145,
      // Per direct request: "RPM :"/"CCM :" should stay on the same line
      // as their own ICD-10 badge(s), not wrap the label onto its own
      // line. Figma's 152 fits the label alone but not label+badge
      // together (each program's row wraps its badges onto a new line
      // below the "RPM :"/"CCM :" text once a column's this narrow, which
      // reads as broken) - widened to comfortably fit a label plus a
      // couple of badges on one line.
      icd_codes_grouped: 230,
      dos: 134,
      // Per direct request: narrowed to 280px (from 350) so that, with the
      // default column selection, one CPT Codes column plus the rest of
      // the table fits on screen without horizontal scrolling.
      cpt_codes_RPM: 280,
      cpt_codes_CCM: 280,
      actions: 67,
      // Restored pre-redesign columns - no Figma reference exists for
      // these yet (SCRUM-688), so these are sized to fit their own header
      // label + sort/info icon rather than measured off the design.
      ccm_consent_date: 120,
      ccm_monitoring_time: 100,
      rpm_monitoring_time: 100,
      rolling_reading_days: 120,
      rpm_setup_date: 165,
      rpm_anchor_date: 170
    };

    // Rough px-per-character estimate for the table's 14px DM Sans body
    // text - good enough to decide "does this need more room than Figma's
    // sample data assumed", not meant to be pixel-exact.
    const CHAR_PX = 7.2;
    const CELL_PADDING_PX = 32; // matches table.js's td { padding: 12px 16px }
    const estimateTextPx = (text = "") =>
      Math.ceil(String(text).length * CHAR_PX) + CELL_PADDING_PX;

    let widestName = 0;
    let widestFacilityProvider = 0;
    let widestInsurance = 0;

    sortedPatients.forEach(row => {
      // Name column also carries the DOB stacked below it - needs to fit
      // whichever line is wider, not just the name itself.
      widestName = Math.max(
        widestName,
        estimateTextPx(formatLastFirstName(nameFieldsOf(row))),
        estimateTextPx(displayDate(row?.dob) || "--")
      );
      widestFacilityProvider = Math.max(
        widestFacilityProvider,
        estimateTextPx(placeOfServiceName(row)),
        estimateTextPx(row?.provider || "--")
      );
      widestInsurance = Math.max(
        widestInsurance,
        estimateTextPx(row?.insurance?.name || row?.insurance_name || "--")
      );
    });

    // CPT Codes columns used to be the table's designated "auto" (leftover-
    // space-absorbing) column, back when the table had far fewer columns
    // and often came in narrower than the viewport. Now that ~18 columns
    // (including the restored RPM/CCM ones) reliably make the table wider
    // than the viewport on its own, that mechanism had nothing consistent
    // to absorb - with two "auto" columns (RPM+CCM both shown) competing
    // against a dozen other explicit-width columns for often near-zero or
    // negative leftover space, browser behavior for that edge case isn't
    // well-defined, and it was producing broken layout (confirmed against
    // a live screenshot: CPT Codes not rendering, rows stretched to ~400px
    // tall). Back to a plain, guaranteed fixed width - simple and
    // predictable regardless of how many other columns exist.
    return {
      ...figmaColumnWidths,
      patient_name: Math.max(figmaColumnWidths.patient_name, widestName),
      facility_provider: Math.max(
        figmaColumnWidths.facility_provider,
        widestFacilityProvider
      ),
      insurance: Math.max(figmaColumnWidths.insurance, widestInsurance)
    };
  }, [sortedPatients]);

  return (
    <div className={styles.billingContainer}>
      <Heading
        title={getHeadingTitle()}
        bodyHeight="auto"
        // SCRUM-627: Heading's shared .comp_body centers content at 95%
        // width with its own padding, which was insetting the table away
        // from the white card's edges (Figma has the table's header/row
        // dividers flush with the card). bodyClassName/className are
        // Heading props just for this - other pages using Heading don't
        // pass them, so they keep the default centered/inset body and
        // 10px inline padding.
        bodyClassName={styles.billingContent}
        className={styles.billingHeadingRoot}
        actions={[
          // SCRUM-627: Lock/Unlock pill per Figma node 6548:1103 - sits next
          // to the "Billings" title, before the month selector. Per direct
          // request, Locked now gets the same red already used for
          // Denied/Deactivated/Inactive elsewhere on this page (#ef4444)
          // instead of the teal/navy treatment shared with Unlocked.
          <Button
            isRound={true}
            size="medium"
            variant="ghost"
            onClick={() => billingLockToggleMutation.mutate()}
            disabled={billingLockToggleMutation.isLoading}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              height: "36px",
              padding: "10px",
              borderRadius: "50px",
              backgroundColor: isLocationLocked ? "#fee2e2" : "#c9e5ee",
              border: isLocationLocked ? "1px solid #ef4444" : "1px solid #48a6c4",
              color: isLocationLocked ? "#ef4444" : "#0d1139",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "14px",
              fontWeight: 400
            }}
          >
            {isLocationLocked ? (
              <>
                <FiLock style={{ fontSize: "20px" }} />
                Locked
              </>
            ) : (
              <>
                <FiUnlock style={{ fontSize: "20px" }} />
                Unlocked
              </>
            )}
          </Button>,
          // <Button
          //   variant="ghost"
          //   onClick={() => refetch()}
          //   disabled={isBillingSummaryLoading || isBillingSummaryFetching}
          //   title="Refresh billing data"
          // >
          //   <FontAwesomeIcon
          //     icon={faSync}
          //     className={`icon-default-size icon-default-color ${isBillingSummaryFetching ? 'fa-spin' : ''}`}
          //   />
          //   Refresh
          // </Button>,
          // SCRUM-627: one merged month+year picker per Figma node
          // 5316:2889 ("July 2026", one pill/one chevron) - a single
          // trigger opens a single panel with both lists side by side,
          // rather than two independent Select dropdowns each popping
          // open on their own. setMonth/setYear are the exact same
          // setters the old Selects called - state and API payload are
          // unchanged, this is presentation only.
          <div ref={monthYearRef} style={{ position: "relative" }}>
            <div
              onClick={() => setMonthYearOpen(o => !o)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
                height: "36px",
                minWidth: "140px",
                padding: "0 10px",
                borderRadius: "6px",
                backgroundColor: "#f8fafd",
                border: "1px solid #c9cbd5",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "12px",
                color: "#0d1139",
                cursor: "pointer"
              }}
            >
              <span>
                {months.find(m => m.value == month)?.label} {year}
              </span>
              {/* summaryDate (built from month/year) is part of the billing
                  summary query's key, so isBillingSummaryFetching reliably
                  flips true on every month/year change - this spinner is a
                  direct, guaranteed signal that the reload actually kicked
                  off, without touching the table's own loading prop (which
                  stays isLoading-only so page/size navigation still feels
                  instant via keepPreviousData). */}
              {isBillingSummaryFetching ? (
                <span
                  className="spinner"
                  style={{ width: "12px", height: "12px", margin: 0, flexShrink: 0 }}
                />
              ) : (
                <FiChevronDown style={{ fontSize: "14px", flexShrink: 0 }} />
              )}
            </div>
            {monthYearOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  right: 0,
                  zIndex: 50,
                  display: "flex",
                  gap: "4px",
                  backgroundColor: "#fff",
                  border: "1px solid #c9cbd5",
                  borderRadius: "8px",
                  padding: "8px",
                  boxShadow: "0px 4px 10px rgba(0,0,0,0.15)"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                    maxHeight: "220px",
                    overflowY: "auto",
                    minWidth: "110px"
                  }}
                >
                  {(parseInt(year) === new Date().getFullYear()
                    ? months.slice(0, new Date().getMonth() + 1)
                    : months
                  ).map(m => (
                    <div
                      key={m.value}
                      onClick={() => handleMonthYearChange(m.value, year)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "13px",
                        color: "#0d1139",
                        fontWeight: m.value == month ? 600 : 400,
                        backgroundColor:
                          m.value == month ? "#f0f2ff" : "transparent"
                      }}
                    >
                      {m.label}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                    maxHeight: "220px",
                    overflowY: "auto",
                    minWidth: "70px",
                    borderLeft: "1px solid #c9cbd5",
                    paddingLeft: "4px"
                  }}
                >
                  {generateYears(2024)
                    .reverse()
                    .map(y => (
                      <div
                        key={y}
                        onClick={() => handleMonthYearChange(month, y)}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: "13px",
                          color: "#0d1139",
                          fontWeight: y === year ? 600 : 400,
                          backgroundColor:
                            y === year ? "#f0f2ff" : "transparent"
                        }}
                      >
                        {y}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>,
          // SCRUM-627: PDF + CSV merged under one "Download" button per
          // Figma node 5316:3102 - clicking it reveals the two format
          // options (comment on the Figma frame). Same handlers/disabled
          // conditions/payload as the previous separate PDF and CSV
          // buttons, just moved behind one trigger.
          <Dropdown
            align="right"
            trigger={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  height: "35px",
                  padding: "6.4px 8px",
                  borderRadius: "17.6px",
                  backgroundColor: "#f8fafd",
                  border: "0.8px solid #c9cbd5",
                  color: "#0d1139",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "12px",
                  cursor: "pointer"
                }}
              >
                <FiDownload style={{ fontSize: "16px" }} />
                Download
              </div>
            }
            onChange={value => {
              if (value === "pdf") {
                const patientStatusParam =
                  patientStatus !== "all" ? `&patient_status=${patientStatus}` : "";
                window.open(
                  `/billings/billing-report?date=${summaryDate}&billingType=${billingType}&location_pk=${locationId}${patientStatusParam}`,
                  "_blank"
                );
              } else if (value === "csv") {
                downloadCSVReport();
              }
            }}
          >
            {billingType !== "all" && (
              // Hidden for the 'all' view, same as before SCRUM-732 - the
              // report itself only knows how to render one program's CPT
              // codes/columns at a time (RPM's 6 codes vs CCM's 5, DOS is
              // RPM-only, etc.), so there's no single PDF that covers a
              // combined RPM+CCM view. Whichever single program tab is
              // active (RPM or CCM) is what gets downloaded.
              <DropdownItem value="pdf" disabled={patientsList.length === 0}>
                PDF
              </DropdownItem>
            )}
            <DropdownItem
              value="csv"
              disabled={
                patientsList.length === 0 ||
                isBillingSummaryLoading ||
                downloadSummaryQuery.isFetching // Disable while downloading/generating
              }
            >
              {downloadSummaryQuery.isFetching ? "Downloading..." : "CSV"}
            </DropdownItem>
          </Dropdown>
        ]}
        content={
          <div>
            {/* Combined Metrics Section */}
           <div className={styles.statusTabContent}>
            <div className={styles.metricsSection}>
              <div className={styles.metricsRow}>
                <>
                  {/* <div className={styles.metricCard}>
                   <div className={styles.metricHeader}>
                     <span>Total RPM Patients</span>
                     <span className={styles.infoIcon}>
                       <FontAwesomeIcon icon={faInfoCircle} />
                     </span>
                   </div>
                   <div className={styles.metricValue}>
                     {statusCounts.total}
                   </div>
                 </div> */}

                  <div className={styles.metricCard}>
                    <div className={styles.metricHeader}>
                      <span>Billed this month</span>
                    </div>
                    <div className={styles.metricValue}>
                      {formatNumber(
                        "currency",
                        billingSummary?.billed_this_month ?? 0,
                        { currency: "USD" }
                      )}
                    </div>
                  </div>

                  <div className={styles.metricCard}>
                    <div className={styles.metricHeader}>
                      <span>Total Billable</span>
                    </div>
                    <div className={styles.metricValue}>
                      {formatNumber(
                        "currency",
                        billingSummary?.total_billable ?? 0,
                        { currency: "USD" }
                      )}
                    </div>
                  </div>
                </>
              </div>

              <div className={styles.metricsSectionDivider} />

              <div className={styles.statusCountsContainer}>
                {renderStatusCounts()}
              </div>
            </div>
            {/* Tabs Section */}
            {/* SCRUM-627: RPM/CCM are checkboxes in Figma (not mutually
                exclusive tabs) - matches toggleView's existing logic, which
                already allows both to be checked at once ("all" view).
                Reverted from a checkbox-styled control back to a plain pill
                button per the "Ready" frame cluster (Rectangle 74 /
                node 5178:1100 and siblings) - confirmed via screenshot that
                the approved design shows RPM/CCM/PCM Billing as solid navy
                pills with no checkbox glyph. The native input stays,
                visually hidden, purely for accessibility/keyboard - the
                toggle behavior itself is unchanged. */}
            <div className={styles.tabsContainer}>
              <label
                className={`${styles.programCheckboxTab} ${
                  selectedViews.rpm ? styles.programCheckboxTabActive : ""
                }`}
              >
                <input
                  type="checkbox"
                  className={styles.programCheckboxInput}
                  checked={selectedViews.rpm}
                  onChange={() => toggleView("rpm")}
                />
                RPM Billing
              </label>
              <label
                className={`${styles.programCheckboxTab} ${
                  selectedViews.ccm ? styles.programCheckboxTabActive : ""
                }`}
              >
                <input
                  type="checkbox"
                  className={styles.programCheckboxInput}
                  checked={selectedViews.ccm}
                  onChange={() => toggleView("ccm")}
                />
                CCM Billing
              </label>

              <div
                style={{ marginLeft: "auto", marginRight: "10px", display: "flex", gap: "10px" }}
              >
                <div ref={searchContainerRef} style={{ position: "relative" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "219px",
                      height: "36px",
                      borderRadius: "18px",
                      border: "1px solid #C9CBD5",
                      background: "#ffffff",
                      padding: "0 10px"
                    }}
                  >
                    <input
                      type="text"
                      value={searchQuery}
                      placeholder="Search patients"
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setSearchDropdownOpen(true);
                      }}
                      onFocus={() => searchQuery && setSearchDropdownOpen(true)}
                      onKeyDown={e => {
                        if (e.key === "Enter") handleSearchIconClick();
                      }}
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "13px",
                        color: "#0d1139"
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleSearchIconClick}
                      title="Search"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        border: "none",
                        background: "transparent",
                        color: "#565975",
                        cursor: "pointer",
                        padding: 0
                      }}
                    >
                      <FiSearch size={18} />
                    </button>
                  </div>

                  {searchDropdownOpen && searchEnabled && (
                    <div
                      style={{
                        position: "absolute",
                        top: "40px",
                        left: 0,
                        width: "219px",
                        maxHeight: "260px",
                        overflowY: "auto",
                        background: "#ffffff",
                        border: "1px solid #C9CBD5",
                        borderRadius: "8px",
                        boxShadow:
                          "0px 10px 20px 0px rgba(0,0,0,0.15), 0px 4px 6px 0px rgba(0,0,0,0.05)",
                        zIndex: 1000
                      }}
                    >
                      {isSearchFetching && (
                        <div style={{ padding: "10px 12px", fontSize: "12px", color: "#94A3B8" }}>
                          Searching...
                        </div>
                      )}
                      {!isSearchFetching && searchPatients.length === 0 && (
                        <div style={{ padding: "10px 12px", fontSize: "12px", color: "#94A3B8" }}>
                          No patients found
                        </div>
                      )}
                      {!isSearchFetching &&
                        visibleSearchPatients.map(patient => (
                          <button
                            key={patient.patient_id}
                            type="button"
                            onClick={() => handleSelectSearchResult(patient)}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              width: "100%",
                              padding: "8px 12px",
                              border: "none",
                              borderBottom: "1px solid #C9CBD5",
                              background: "transparent",
                              textAlign: "left",
                              cursor: "pointer"
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%"
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#0d1139"
                                }}
                              >
                                {formatLastFirstName(nameFieldsOf(patient))}
                              </span>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "11px",
                                  fontWeight: 500,
                                  // Match .deactivatedBadge/.inactiveBadge's colors
                                  // (billing.module.css) used for this same status in
                                  // the main table - "Deactivated" blue, "Inactive"/
                                  // anything else red.
                                  color:
                                    patient.status?.toLowerCase() === "active"
                                      ? "#10b981"
                                      : patient.status?.toLowerCase() === "deactivated"
                                      ? "#0284c7"
                                      : "#ef4444"
                                }}
                              >
                                {/* SCRUM-685: conceptual definition of the
                                    status itself - distinct from SCRUM-684's
                                    per-patient hover popup (date + reason),
                                    which lives on the table row, not here.
                                    Icon sits in front of the status text per
                                    direct request. */}
                                {SEARCH_STATUS_INFO[patient.status?.toLowerCase()] && (
                                  <Tooltip
                                    content={SEARCH_STATUS_INFO[patient.status?.toLowerCase()]}
                                    position="top"
                                    tooltipStyle={{ zIndex: TOOLTIP_Z_INDEX }}
                                  >
                                    <FiInfo
                                      size={11}
                                      style={{ marginTop: "5px" }}
                                    />
                                  </Tooltip>
                                )}
                                {capitalize(patient.status)}
                              </span>
                            </div>
                            {patient.dob && (
                              <span style={{ fontSize: "11px", color: "#565975" }}>
                                {formatUSADateDash(patient.dob)}
                              </span>
                            )}
                          </button>
                        ))}
                      {/* SCRUM-685: truncation indicator when there are more
                          than 10 matches - visibleSearchPatients itself is
                          already capped, searchPatients (unsliced) carries
                          the true count. */}
                      {!isSearchFetching && searchPatients.length > 10 && (
                        <div
                          style={{
                            padding: "8px 12px",
                            fontSize: "11px",
                            color: "#94A3B8",
                            textAlign: "center",
                            fontStyle: "italic"
                          }}
                        >
                          Showing 10 of {searchPatients.length} — refine your search
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Select
                  value={patientStatus}
                  onChange={value => setPatientStatus(value)}
                >
                  <SelectTrigger
                    style={{ width: "240px", backgroundColor: "white" }}
                  >
                    <SelectValue placeholder="Select Status">
                      <span style={{ marginRight: "10px" }}>
                        {patientStatus === "active"
                          ? "Active Patients"
                          : patientStatus === "inactive"
                          ? "Inactive Patients"
                          : "All Patients"}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Patients</SelectItem>
                    <SelectItem value="active">Active Patients</SelectItem>
                    <SelectItem value="inactive">Inactive Patients</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            </div>
            {/* Table Section */}
            <div className={styles.tableWrapper} ref={tableWrapperRef}>
              <Table
                columns={columns}
                data={sortedPatients}
                // SCRUM-683: Figma's footer (node 6496:2217) is a single
                // row - pagination arrows+"of N" / A-Z letters / Result per
                // page - not table.js's windowed page-number footer, so
                // Billing now renders its own footer below instead of
                // opting into table.js's built-in one. `data` is already
                // exactly one server-paginated page's worth of rows either
                // way (table.js's own `pagination` prop only ever gates
                // which footer UI it shows, never which rows it slices -
                // see visibleRows in table.js), so turning this off doesn't
                // change what renders inside the table itself.
                pagination={false}
                isRowClickable={false}
                showCheckboxes={false}
                // SCRUM-987: empty list while a Status Count pill is selected.
                emptyMessage={
                  billingStatus
                    ? "No patients with this billing status for the selected month."
                    : undefined
                }
                // SCRUM-3 AC15: Kalioundji's numbered pagination also shows
                // the loading indicator over the rows during a plain page
                // jump (isBillingSummaryFetching), not just on first load or
                // a filter change - every other account keeps today's
                // stale-while-revalidate paging (old page stays visible,
                // no spinner, via keepPreviousData) unchanged.
                loading={
                  isBillingSummaryLoading ||
                  isFilterTransitioning ||
                  (isKalioundjiAccount && isBillingSummaryFetching)
                }
                // SCRUM-627: alternating row tint per Figma nodes
                // 5316:2831/2832/2833 (rgba(13,17,57,0.04) bands over the
                // 2nd/4th/6th row) - table.js's rowColor hook already
                // existed but nothing passed it, so rows were plain white.
                // Using the opaque equivalent (rgba(13,17,57,0.04) flattened
                // over white: 255-0.04*(255-13/17/57)) rather than the
                // translucent rgba itself - renders identically over the
                // page's white background, but table.js also mirrors this
                // onto the frozen Name column's background (see
                // --frozen-cell-bg there), which needs to be fully opaque
                // to mask content scrolling underneath it. A translucent
                // color there would always show that content bleeding
                // through, no matter what z-index/stacking is applied.
                // SCRUM-684: entire row greyed out for the Inactive Patients
                // view, per Devon Gaur's comment on the ticket (2026-08-28) -
                // "I also requested that the entire inactive patient row be
                // greyed out so the user can immediately understand
                // visually that the patient is inactive." patientStatus is
                // the page-level filter, so every row shares this state.
                rowColor={(row, index) =>
                  patientStatus === "inactive"
                    ? "#eceef1"
                    : index % 2 === 1
                    ? "rgb(245, 245, 247)"
                    : "transparent"
                }
                primaryHeader={true}
                // Header renders in its own table (vertical scrollbar
                // belongs only to the body rows).
                // Per direct request: one scroller (the page itself), not
                // a bounded inner box - disableInternalScroll instead of
                // stickyTableRegion. Header (position:sticky, offset below
                // the fixed nav) and the expanded footer (position:sticky
                // to the bottom, see table.js) both still pin to the
                // viewport as the page scrolls; only the rows scroll past
                // them, same visual result as before, just without the
                // table owning its own nested scroll region.
                splitHeaderScroll={true}
                disableInternalScroll={true}
                // SCRUM-732: the floating scrollbar below is the only
                // horizontal scrollbar meant to be visible.
                hideHorizontalScrollbar={true}
                // SCRUM-630/631: per-column min-widths so content-heavy
                // columns (stacked Facility/Provider, ICD-10 badges, wrapping
                // CPT pill grids) keep the Figma layout instead of being
                // squeezed to the table's generic 100px default - the table
                // then scrolls horizontally (already built into Table via
                // .split_scroll_outer{overflow-x:auto}, opted into by
                // splitHeaderScroll above) instead of the columns collapsing
                // or content clipping/wrapping mid-word. See
                // dynamicColumnWidths above for how these are computed -
                // this prop is only ever passed by Billing, so none of it
                // can affect any other page using the shared Table.
                columnWidths={columnWidths}
                // SCRUM-683: flashes the row the backend flagged
                // "highlight": true after an A-Z jump (see the
                // handleAlphabetLetterClick/highlightedPatientId effects).
                getRowClassName={row =>
                  highlightedPatientId != null &&
                  String(row?.patient_id) === String(highlightedPatientId)
                    ? styles.highlightedRow
                    : ""
                }
              />
              {/* SCRUM-683/table footer: single row matching Figma node
                  6496:2217 exactly - "‹ [page] of N ›" | A-Z letters |
                  "Result per page [size]" - rather than table.js's own
                  windowed page-number footer, which lays these out
                  differently. Sticky-to-bottom styling mirrors table.js's
                  own disableInternalScroll footer treatment (the page, not
                  a bounded inner box, owns scroll here - see the
                  disableInternalScroll comment on Table above). */}
              <div className={styles.tableFooterBar} ref={footerBarRef}>
                {/* SCRUM-3: Kalioundji only - numbered pagination (arrows +
                    windowed page buttons + direct page-entry). Every other
                    account keeps this exact Back/[page box]/Next footer
                    unchanged (AC4). */}
                {isKalioundjiAccount ? (
                  <KalioundjiPagination
                    currentPage={meta?.current_page || 1}
                    totalPages={meta?.total_pages || 1}
                    isFetching={isBillingSummaryFetching}
                    onPageChange={handlePageChange}
                    // Same deps that reset `page` back to 1 elsewhere - a
                    // fresh view (new tab/status/month/page size) starts
                    // back in the default empty-box state (AC8), not
                    // carrying over a previous view's boxed current page.
                    resetKey={`${billingType}|${patientStatus}|${summaryDate}|${pageSize}`}
                  />
                ) : (
                  <div className={styles.footerPagination}>
                    <button
                      type="button"
                      className={styles.footerPaginationControl}
                      onClick={() => handlePageChange((meta?.current_page || 1) - 1)}
                      disabled={(meta?.current_page || 1) <= 1 || isBillingSummaryFetching}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} size="xs" />
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      aria-label="Go to page"
                      className={`${styles.footerPageBox} ${styles.footerPageInput}`}
                      value={pageInputValue}
                      onChange={handlePageInputChange}
                      onKeyDown={handlePageInputKeyDown}
                      onBlur={commitPageInput}
                      disabled={isBillingSummaryFetching}
                    />
                    <span className={styles.footerOfText}>of {meta?.total_pages || 1}</span>
                    <button
                      type="button"
                      className={styles.footerPaginationControl}
                      onClick={() => handlePageChange((meta?.current_page || 1) + 1)}
                      disabled={
                        (meta?.current_page || 1) >= (meta?.total_pages || 1) ||
                        isBillingSummaryFetching
                      }
                    >
                      <FontAwesomeIcon icon={faChevronRight} size="xs" />
                    </button>
                  </div>
                )}

                {/* SCRUM-683: A-Z alphabetical nav - jumps to the page
                    containing the first patient whose last name starts with
                    the clicked letter (jump_to_letter), per the API spec
                    message. Matches Figma minus the "All"/"0-9" chips it
                    doesn't show. Letters with no matching patients grey out
                    per SCRUM-825's `available_letters` field - if that field
                    is ever missing from a response (e.g. an older backend
                    version), every letter stays clickable rather than
                    silently graying out everything. */}
                <div className={styles.alphabetNavBar}>
                  {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map(letter => {
                    const isAvailable =
                      !Array.isArray(availableLetters) ||
                      availableLetters.includes(letter);
                    return (
                      <button
                        key={letter}
                        type="button"
                        className={`${styles.alphabetNavLetter} ${
                          activeLetter === letter ? styles.alphabetNavLetterActive : ""
                        }`}
                        onClick={() => handleAlphabetLetterClick(letter)}
                        disabled={isBillingSummaryFetching || !isAvailable}
                      >
                        {letter}
                      </button>
                    );
                  })}
                </div>

                <div className={styles.footerResultPerPage}>
                  <span className={styles.footerResultPerPageLabel}>Result per page</span>
                  <Select
                    value={String(pageSize)}
                    onChange={value => handlePageSizeChange(Number(value))}
                  >
                    <SelectTrigger
                      style={{
                        width: "72px",
                        height: "32px",
                        border: "1px solid #d0d0d0",
                        borderRadius: "6px",
                        backgroundColor: "white",
                        fontSize: "13px",
                        padding: "4px 8px"
                      }}
                    >
                      <SelectValue placeholder="Page size">{pageSize}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map(size => (
                        <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        }
      ></Heading>

      {/* SCRUM-634/635: Mark as Denied / Mark as Appealed popup */}
      <Dialog open={markStatusModal.open} setOpen={closeMarkStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={styles.alertTitle}>
              {markStatusModal.mode === "denied" ? "Mark as Denied" : "Mark as Appealed"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className={styles.markStatusPatientInfo}>
              <div>
                <span className={styles.markStatusInfoLabel}>Name:</span>{" "}
                {formatLastFirstName(nameFieldsOf(markStatusModal.row))}
              </div>
              <div>
                <span className={styles.markStatusInfoLabel}>DOB:</span>{" "}
                {displayDate(markStatusModal.row?.dob)}
              </div>
              <div>
                <span className={styles.markStatusInfoLabel}>Place of Service:</span>{" "}
                {placeOfServiceName(markStatusModal.row)}
              </div>
              <div>
                <span className={styles.markStatusInfoLabel}>Provider:</span>{" "}
                {markStatusModal.row?.provider || "--"}
              </div>
              <div>
                <span className={styles.markStatusInfoLabel}>Insurance:</span>{" "}
                {markStatusModal.row?.insurance?.name ||
                  markStatusModal.row?.insurance_name ||
                  "--"}
              </div>
            </div>

            {getMarkStatusItems(markStatusModal.row, markStatusModal.mode).length === 0 ? (
              <p style={{ padding: "8px 0", fontSize: "14px", color: "#565975" }}>
                {markStatusModal.mode === "denied"
                  ? "No billed or appealed code available to deny"
                  : "No denied code available to appeal"}
              </p>
            ) : (
              <>
                {selectedViews.rpm && (
                  <div className={styles.markStatusCodeSection}>
                    <p className={styles.markStatusSectionLabel}>RPM Codes</p>
                    <div className={styles.markStatusCodeGrid}>
                      {(markStatusModal.row?.rpm_billings || [])
                        .filter(
                          b =>
                            rpmCodes.includes(b.code) &&
                            isEligibleForMarkStatus(b, markStatusModal.mode)
                        )
                        .map((b, i) => {
                          const itemKey = `rpm-${i}-${b.code}`;
                          const currentLabel = statusToLabelMap[b.status] || statusToLabelMap.default;
                          return (
                            <label key={itemKey} className={styles.markStatusCodeItem}>
                              <input
                                type="checkbox"
                                checked={markStatusModal.selectedIds.includes(itemKey)}
                                onChange={() => toggleMarkStatusCode(itemKey)}
                              />
                              <span>
                                {b.code}
                                {b.units > 1 ? ` x${b.units}` : ""}
                              </span>
                              <span
                                className={`${styles.statusPill} ${styles[currentLabel.toLowerCase()]}`}
                              >
                                <span className={styles.pillDot} />
                                {currentLabel}
                              </span>
                            </label>
                          );
                        })}
                    </div>
                  </div>
                )}

                {selectedViews.ccm && (
                  <div className={styles.markStatusCodeSection}>
                    <p className={styles.markStatusSectionLabel}>CCM Codes</p>
                    <div className={styles.markStatusCodeGrid}>
                      {(markStatusModal.row?.ccm_billings || [])
                        .filter(
                          b =>
                            ccmCodes.includes(b.code) &&
                            isEligibleForMarkStatus(b, markStatusModal.mode)
                        )
                        .map((b, i) => {
                          const itemKey = `ccm-${i}-${b.code}`;
                          const currentLabel = statusToLabelMap[b.status] || statusToLabelMap.default;
                          return (
                            <label key={itemKey} className={styles.markStatusCodeItem}>
                              <input
                                type="checkbox"
                                checked={markStatusModal.selectedIds.includes(itemKey)}
                                onChange={() => toggleMarkStatusCode(itemKey)}
                              />
                              <span>
                                {b.code}
                                {b.units > 1 ? ` x${b.units}` : ""}
                              </span>
                              <span
                                className={`${styles.statusPill} ${styles[currentLabel.toLowerCase()]}`}
                              >
                                <span className={styles.pillDot} />
                                {currentLabel}
                              </span>
                            </label>
                          );
                        })}
                    </div>
                  </div>
                )}
              </>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={closeMarkStatusModal}>
              Cancel
            </Button>
            <Button
              className={styles.markStatusConfirmBtn}
              onClick={confirmMarkStatus}
              disabled={isBulkStatusUpdating || markStatusModal.selectedIds.length === 0}
            >
              {markStatusModal.mode === "denied" ? "Mark as Denied" : "Mark as Appealed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Modal */}
      <Dialog
        open={showDownloadModal}
        setOpen={() => setShowDownloadModal(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={styles.alertTitle}>Billing</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className={styles.modalBody}>
              <div className={styles.downloadSection}>
                <h4>Download Audit Report</h4>

                <p>
                  Generate Audit Report for <b>{formatLastFirstName(nameFieldsOf(patient))}</b>
                </p>

                <div className={styles.auditReportContainer}>
                  <div className={styles.auditReportHeader}>
                    <div className={styles.auditReportIcon}>
                      <FontAwesomeIcon
                        icon={faFileInvoice}
                        size="3x"
                        color="#0f1e59"
                      />
                    </div>
                    <div className={styles.auditReportInfo}>
                      <h5>Audit Report</h5>
                      <p>
                        Select a monthly/year and receive a monthly activity
                        summary.
                      </p>
                    </div>
                  </div>

                  {billingType === "all" && (
                    <div style={{ marginBottom: "15px" }}>
                      <p
                        style={{
                          marginBottom: "5px",
                          fontSize: "14px",
                          fontWeight: "500"
                        }}
                      >
                        Report Type
                      </p>
                      <Select
                        onChange={val => setSelectedReportType(val)}
                        value={selectedReportType}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Report Type">
                            {selectedReportType === "rpm"
                              ? "RPM Audit Report"
                              : selectedReportType === "ccm"
                                ? "CCM Audit Report"
                                : "Select Report Type"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rpm">RPM Audit Report</SelectItem>
                          <SelectItem value="ccm">CCM Audit Report</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className={styles.dateRangeContainer}>
                    <Select
                      onChange={value => setAuditReportMonth(value)}
                      value={auditReportMonth}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select month">
                          {months.find(m => m.value == auditReportMonth)?.label}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {auditReportAvailableMonths.map(month => (
                          <SelectItem key={month.value} value={month.value}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      onChange={value => setAuditReportYear(value)}
                      value={auditReportYear}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select year">
                          {auditReportYear ?? "Select year"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {generateYears(2024)
                          .reverse()
                          .map(year => (
                            <SelectItem key={year} value={year}>
                              {year}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(billingType !== "all" || selectedReportType) && (
                    <div className={styles.auditReportActions}>
                      <Button
                        variant="default"
                        className={styles.downloadPdfButton}
                        onClick={handleDownloadPDF}
                      >
                        <FontAwesomeIcon
                          icon={faDownload}
                          className={styles.buttonIcon}
                        />
                        Download PDF
                      </Button>

                      <Button
                        variant="default"
                        className={styles.sharePdfButton}
                        onClick={handleSharePDF}
                      >
                        <FontAwesomeIcon
                          icon={faShareAlt}
                          className={styles.buttonIcon}
                        />
                        Share PDF
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setShowDownloadModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Suspense fallback={<Spinner />}>
        <SharingModal
          isOpen={isSharingModalOpen}
          setIsOpen={state => {
            setIsSharingModalOpen(state);
            setSharing(false);
          }}
          onShare={handleShare}
          isSharing={sharing}
          title="Share Billing Report"
          shareButtonText="Share Report"
          showExternalProvider={true}
          showFormatSelector={true}
          availableFormats={["pdf"]}
          defaultFormat="pdf"
          defaultShareType="provider"
          patientId={patientId}
        />
      </Suspense>

      {/* Appeal Letter Modal */}
      <AppealLetterModal
        isOpen={showAppealLetterModal}
        onClose={handleCloseAppealLetterModal}
        onGenerateAppeal={handleGenerateAppealLetter}
        onDownloadAppeal={handleDownloadAppealLetter}
        programType={currentServiceType}
        patientName={formatLastFirstName(nameFieldsOf(selectedPatientForAppeal))}
        lastGeneratedDate={lastAppealGeneratedDate}
        isDownloadEnabled={appealLetterGenerated}
        patientId={selectedPatientForAppeal?.patient_id}
        getPatientDocumentsById={getPatientDocumentsById}
      />

      {/* Appeal Letter Email Form */}
      <AppealLetterEmailForm
        isOpen={showAppealEmailForm}
        onClose={handleCloseAppealEmailForm}
        onSendEmail={handleSendAppealEmail}
        patient={selectedPatientForAppeal}
        programType={currentServiceType}
        billingPeriod={`${months.find(m => m.value == month)?.label} ${year}`}
        deniedCodes={getDeniedCodes(selectedPatientForAppeal, currentServiceType)}
        isSending={isSendingAppealEmail}
      />

      {/* SCRUM-732: column-picker popup, portaled to document.body. Rendered
          directly here (not inside the `columns` useMemo above, where it
          used to live) so it always reflects the latest state on every
          render - no dependency array to keep in sync with what it reads. */}
      {columnsMenuOpen &&
        createPortal(
          <div
            ref={columnsMenuContentRef}
            style={{
              position: "fixed",
              top: `${columnsMenuPos.top}px`,
              left: `${columnsMenuPos.left}px`,
              zIndex: 1000,
              minWidth: "220px",
              backgroundColor: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              boxShadow:
                "0px 10px 20px 0px rgba(0,0,0,0.15), 0px 4px 6px 0px rgba(0,0,0,0.05)",
              padding: "8px",
              textAlign: "left"
            }}
          >
            <div style={{ display: "flex", gap: "8px", padding: "0 4px 8px" }}>
              <button
                type="button"
                onClick={handleResetColumns}
                style={{
                  flex: 1,
                  padding: "5px 0",
                  borderRadius: "13px",
                  border: "1px solid #C9CBD5",
                  background: "#fff",
                  color: "#0D1139",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer"
                }}
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleSaveColumns}
                style={{
                  flex: 1,
                  padding: "5px 0",
                  borderRadius: "13px",
                  border: "none",
                  background: "#0D1139",
                  color: "#fff",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer"
                }}
              >
                Save
              </button>
            </div>
            <div style={{ borderBottom: "1px solid #C9CBD5", margin: "0 0 4px" }} />
            {CONFIGURABLE_COLUMNS.filter(
              col =>
                col.group === "generic" ||
                (col.group === "ccm" && selectedViews.ccm) ||
                (col.group === "rpm" && selectedViews.rpm)
            ).map(col => (
              <label
                key={col.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px",
                  borderRadius: "6px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#0d1139",
                  cursor: "pointer"
                }}
              >
                <input
                  type="checkbox"
                  checked={pendingVisibleColumns[col.key] !== false}
                  onChange={() => togglePendingColumn(col.key)}
                />
                {col.label}
              </label>
            ))}
          </div>,
          document.body
        )}

      {/* Floating horizontal scrollbar - pinned just above the sticky
          pagination footer (not the table's own bottom edge, which can be
          far below the fold on a long list) so the user never has to
          scroll all the way down just to scroll the table sideways. A
          custom-drawn track+thumb (no native overflow-x scrollbar anywhere
          here) - this is the only horizontal scrollbar rendered. */}
      {scrollTrack.contentWidth > scrollTrack.width && (
        <div
          onMouseDown={handleTrackMouseDown}
          style={{
            position: "fixed",
            top: `${footerTop - 6}px`,
            left: `${scrollTrack.left}px`,
            width: `${scrollTrack.width}px`,
            height: "6px",
            // Below the "Result per page" Select's opened dropdown, which
            // computes its own z-index at runtime as its nearest ancestor
            // with an explicit z-index (.tableFooterBar's 2) + 100 = 102
            // (see components/ui/select's getComputedZIndex) - this bar
            // sits directly above that footer, so it must stay under 102
            // or it renders on top of the opened options list. Still well
            // above ordinary table content, which has no z-index of its
            // own (auto).
            zIndex: 50,
            background: "transparent",
            cursor: "pointer"
          }}
        >
          <div
            onMouseDown={handleThumbMouseDown}
            style={{
              position: "absolute",
              top: 0,
              left: `${thumbLeft}px`,
              width: `${thumbWidth}px`,
              height: "6px",
              borderRadius: "3px",
              background: "#0D1139",
              cursor: "grab"
            }}
          />
        </div>
      )}

      {/* Back-to-top - needed now that the page itself scrolls (see
          disableInternalScroll on Table above) rather than a bounded
          region, so the top of the page can end up far above the
          viewport on a long list. */}
      {showScrollTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          title="Back to top"
          style={{
            position: "fixed",
            bottom: "50px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "35px",
            height: "35px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color:"#0d1139",
            backgroundColor: "#ffffff",
            border: "0.8px solid #c9cbd5",
            boxShadow: "0px 4px 10px rgba(0,0,0,0.25)",
            cursor: "pointer",
            zIndex: 1000
          }}
        >
          <FiArrowUp size={20} style={{ flexShrink: 0 }} />
        </button>
      )}
    </div>
  );
}

const exportToCSV = (data, filename = "export.csv", headers) => {
  const csvRows = [];

  csvRows.push(headers.join(","));

  data.forEach(row => {
    const values = headers.map(header => `"${row[header]}"`);
    csvRows.push(values.join(","));
  });

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.setAttribute("href", url);
  a.setAttribute("download", filename);
  a.click();
};
export default BillingPage;